import { useEffect, useState, useCallback, useMemo, type CSSProperties } from 'react'
import { api, type Assistant, type AssistantStageCapability, type ResourceSettings } from '../api/client'
import { ConfirmDialog, Toast, UndoToast } from '../components/ConfirmDialog'
import { PayrollClaimsPanel } from '../components/PayrollClaimsPanel'
import { useSearchParams } from 'react-router-dom'
import { AssistantOnboardingWizard } from '../components/AssistantOnboardingWizard'
import { BonusEditModal, type BonusEditRecord } from '../components/BonusEditModal'
import { todayJst, thisMonthJst } from '../utils/jst'
import { useIsMobile } from '../hooks/useIsMobile'
import { fetchMonthScheduleEvents, groupEventsByDate, type ScheduleEvent } from '../utils/scheduleEvents'

function fmt(n: number | null | undefined) { return (n ?? 0).toLocaleString('ja-JP') }
function diffMinutes(start: string, end: string): number {
  const [sh, sm] = start.split(':').map(Number)
  const [eh, em] = end.split(':').map(Number)
  if ([sh, sm, eh, em].some((v) => Number.isNaN(v))) return 0
  return Math.max(0, (eh * 60 + em) - (sh * 60 + sm))
}
const NORMAL_PAID_MINUTES = 510
const FULLTIME_CLOCK_MINUTES = 600 // 現在は未使用（将来の給与ルール調整用に保持）
void FULLTIME_CLOCK_MINUTES
const BREAK_MINUTES = 90        // フルタイム勤務の休憩時間デフォルト（未指定時）
const BREAK_MINUTES_MAX = 240   // 休憩時間の手動変更時の上限（異常値防止）

// フルタイム勤務の残業分数。「実働時間が通常勤務時間(510分)を超えた分」で算出する
// （退勤時刻が20:00を超えたかどうかでは判定しない）。保存時はサーバー側でも同じ考え方で再計算される。
// breakMinutes は通常90分固定だが、必要な場合のみ手動変更できる。
function calcFullTimeOvertimeMinutes(checkIn: string, checkOut: string, breakMinutes = BREAK_MINUTES): number {
  const totalMin = diffMinutes(checkIn, checkOut) - breakMinutes
  return Math.max(0, totalMin - NORMAL_PAID_MINUTES)
}

/** 休憩時間分数を "1.5時間" / "60分" のように表示する（90分の時だけ時間表記） */
function formatBreakMinutes(minutes: number): string {
  return minutes === BREAK_MINUTES ? '1.5時間' : `${minutes}分`
}

// 時間勤務・時短勤務の勤務時間(分) = 出退勤差分 − 休憩時間。残業概念は持たない
// （アシスタント本人側の入力画面と揃える）。
// 以前は実働時間の一部を勝手に「残業」として分離し、work_minutesを通常勤務基準(510分)で
// 頭打ちにしていたため、長時間シフトの給与が意図せず減ってしまう不具合があった。
function calcPartTimeWorkMinutes(checkIn: string, checkOut: string, breakMinutes = 0): number {
  return Math.round(Math.max(0, diffMinutes(checkIn, checkOut) - Math.max(0, Number(breakMinutes) || 0)))
}
function normalizeAttendanceFormForSubmit(form: any) {
  if (form.work_type !== 'part_time') {
    const breakMinutes = Math.min(BREAK_MINUTES_MAX, Math.max(0, Math.round(Number(form.break_minutes) || 0)))
    return {
      workMinutes: 0,
      overtimeMin: calcFullTimeOvertimeMinutes(form.check_in, form.check_out, breakMinutes),
      breakMinutes,
    }
  }
  const breakMinutes   = Math.max(0, Number(form.break_minutes) || 0)
  const manualMinutes  = Math.max(0, Number(form.work_minutes) || 0)
  const computedMinutes = calcPartTimeWorkMinutes(form.check_in, form.check_out, breakMinutes)
  return {
    workMinutes: manualMinutes > 0 ? manualMinutes : computedMinutes,
    overtimeMin: 0,
    breakMinutes,
  }
}
function isPayrollEnabled(a: any): boolean {
  if (a?.payroll_enabled === 0) return false
  // 古いAPIレスポンスで payroll_enabled が返らない場合の暫定互換
  if (a?.name === '大嶋' && Number(a?.daily_rate ?? 0) === 0) return false
  return true
}
const MONTH_KEY = 'mangaos_month'

function getSavedMonth(): string {
  try {
    const saved = localStorage.getItem(MONTH_KEY)
    if (saved && /^\d{4}-\d{2}$/.test(saved)) return saved
  } catch {
    // ignore
  }
  return thisMonthJst()
}

function RoleBadge({ role }: { role: string }) {
  const map: Record<string, [string, string]> = {
    background: ['背景', 'badge-blue'],
    finishing:  ['仕上げ', 'badge-green'],
    ai_art:     ['AI作画', 'badge-amber'],
    research:   ['資料', 'badge-gray'],
    general:    ['全般', 'badge-gray'],
  }
  const [label, cls] = map[role] ?? ['—', 'badge-gray']
  return <span className={`badge ${cls}`}>{label}</span>
}

function PcBadge({ pcType }: { pcType?: string | null }) {
  if (pcType === 'windows') return <span className="badge badge-blue" style={{ fontSize: 10 }}>Win</span>
  if (pcType === 'mac')     return <span className="badge badge-gray" style={{ fontSize: 10 }}>Mac</span>
  return <span className="badge badge-gray" style={{ fontSize: 10, opacity: 0.55 }}>PC未設定</span>
}

// ================================================================
// 退職管理モーダル
// ================================================================
type RetireAction = 'leaving' | 'retired' | 'reactivate'

function RetirementModal({ assistant, action, onClose, onSaved }: {
  assistant: Assistant
  action: RetireAction
  onClose: () => void
  onSaved: () => void
}) {
  const today = todayJst()
  const [form, setForm] = useState({
    retired_at:       today,
    retirement_note:  '',
    login_enabled:    0,
    shift_enabled:    0,
    report_enabled:   0,
    // 「退職済みにする」操作の時点ではまだ最終精算が済んでいない場合があるため、
    // leaving/retired どちらもデフォルトはON（給与対象に残す）。
    // 精算完了後に外す運用はチェックボックスの補足文言の通り（管理者が手動でOFFにする）。
    payroll_enabled:  1,
    final_settled_at: action === 'retired' ? today : '',
  })
  const [confirmed, setConfirmed] = useState(false)
  const [saving,    setSaving]    = useState(false)
  const [error,     setError]     = useState<string | null>(null)

  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }))

  const handleSubmit = async () => {
    setSaving(true); setError(null)
    try {
      if (action === 'reactivate') {
        await api.assistants.reactivate(assistant.id)
      } else {
        await api.assistants.retire(assistant.id, {
          employment_status: action,
          retired_at:        form.retired_at   || null,
          retirement_note:   form.retirement_note || null,
          login_enabled:     form.login_enabled,
          shift_enabled:     form.shift_enabled,
          report_enabled:    form.report_enabled,
          payroll_enabled:   form.payroll_enabled,
          ...(action === 'retired' && form.final_settled_at
            ? { final_settled_at: form.final_settled_at }
            : {}),
        })
      }
      onSaved()
    } catch (e: any) {
      setError(e.message ?? 'エラーが発生しました')
    } finally { setSaving(false) }
  }

  const title =
    action === 'leaving'    ? '退職予定にする' :
    action === 'retired'    ? '退職済みにする' :
    '現役に戻す'

  const confirmMsg =
    action === 'leaving'
      ? `このアシスタントを退職予定にします。\nログイン、日報、シフト対象から外れます。\nただし、最終給与のため給与対象には残せます。\n過去の給与・日報・シフト履歴は削除されません。`
    : action === 'retired'
      ? `このアシスタントを退職済みにします。\n通常の給与作成対象からも外れます。\n最終給与・交通費の精算が完了しているか確認してください。\n過去の給与・日報・シフト履歴は削除されません。`
    : `このアシスタントを現役に戻します。\nログイン、日報、シフト、給与対象が再度有効になります。`

  const msgColor = action === 'reactivate' ? 'var(--green-light)' : 'var(--amber-light)'
  const msgBorder = action === 'reactivate' ? 'rgba(45,122,58,0.3)' : 'rgba(176,122,18,0.3)'

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <div className="modal-title">{assistant.name} を{title}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{
            background: msgColor,
            border: `1px solid ${msgBorder}`,
            borderRadius: 8, padding: '12px 16px', marginBottom: 16,
            fontSize: 13, whiteSpace: 'pre-line', lineHeight: 1.7,
          }}>
            {confirmMsg}
          </div>

          {action !== 'reactivate' && (
            <>
              <div className="grid-2" style={{ marginBottom: 12 }}>
                <div className="form-group">
                  <label>退職日</label>
                  <input type="date" value={form.retired_at}
                    onChange={(e) => set('retired_at', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>退職メモ（任意）</label>
                  <input type="text" placeholder="本人都合による退職 など"
                    value={form.retirement_note}
                    onChange={(e) => set('retirement_note', e.target.value)} />
                </div>
              </div>

              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 8 }}>
                各フラグの設定
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input type="checkbox"
                    checked={form.login_enabled === 0}
                    onChange={(e) => set('login_enabled', e.target.checked ? 0 : 1)} />
                  ログインを停止する
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>（推奨）</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input type="checkbox"
                    checked={form.shift_enabled === 0}
                    onChange={(e) => set('shift_enabled', e.target.checked ? 0 : 1)} />
                  シフト対象から外す
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>（推奨）</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input type="checkbox"
                    checked={form.report_enabled === 0}
                    onChange={(e) => set('report_enabled', e.target.checked ? 0 : 1)} />
                  日報対象から外す
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>（推奨）</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input type="checkbox"
                    checked={form.payroll_enabled === 1}
                    onChange={(e) => set('payroll_enabled', e.target.checked ? 1 : 0)} />
                  給与対象に残す
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {action === 'leaving' ? '（最終給与のため推奨）' : '（最終精算完了後は外してください）'}
                  </span>
                </label>
              </div>

              {action === 'retired' && (
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label>最終精算完了日（任意）</label>
                  <input type="date" value={form.final_settled_at}
                    onChange={(e) => set('final_settled_at', e.target.value)} />
                </div>
              )}
            </>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink-2)', marginTop: 4 }}>
            <input type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)} />
            内容を確認しました
          </label>
          {error && (
            <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8,
              background: 'var(--red-light)', borderRadius: 6, padding: '8px 12px' }}>
              ⚠ {error}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
          <button
            className={`btn ${action === 'reactivate' ? 'btn-primary' : 'btn-danger'}`}
            onClick={handleSubmit}
            disabled={saving || !confirmed}
          >
            {saving ? '処理中...' : title}
          </button>
        </div>
      </div>
    </div>
  )
}

// ================================================================
// ソートヘルパー
// ================================================================
type SortState = { key: string; dir: 'asc' | 'desc' }

function SortTh({ label, colKey, sort, onSort, style }: {
  label: string
  colKey: string
  sort: SortState
  onSort: (key: string) => void
  style?: CSSProperties
}) {
  const active = sort.key === colKey
  return (
    <th
      onClick={() => onSort(colKey)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
    >
      {label}
      <span style={{ marginLeft: 3, opacity: active ? 1 : 0.2, fontSize: 10 }}>
        {active && sort.dir === 'desc' ? '▼' : '▲'}
      </span>
    </th>
  )
}

// ================================================================
// Phase 5-C: スキル設定
// ================================================================
const STAGE_KEYS_DEF = [
  { key: 'sketch',     label: '下書き' },
  { key: 'background', label: '背景' },
  { key: 'finishing',  label: '仕上げ' },
  { key: 'ai_art',     label: 'AI作画' },
  { key: 'research',   label: '資料' },
  { key: 'general',    label: '汎用' },
] as const

type CapFormRow = {
  stage_key:           string
  skill_level:         number
  preference_order:    number
  capacity_multiplier: number
  is_active:           number
  note:                string
}

function initCapFormRows(assistant: Assistant): CapFormRow[] {
  const existing = assistant.stage_capabilities ?? []
  return STAGE_KEYS_DEF.map(({ key }) => {
    const found = existing.find((c: AssistantStageCapability) => c.stage_key === key)
    if (found) {
      return {
        stage_key:           key,
        skill_level:         found.skill_level,
        preference_order:    found.preference_order,
        capacity_multiplier: found.capacity_multiplier,
        is_active:           found.is_active,
        note:                found.note ?? '',
      }
    }
    const isMainRole = key === assistant.role_type
    return {
      stage_key:           key,
      skill_level:         isMainRole ? 3 : 1,
      preference_order:    50,
      capacity_multiplier: 1.0,
      is_active:           isMainRole ? 1 : 0,
      note:                '',
    }
  })
}

// ================================================================
// アシスタント編集モーダル
// ================================================================
function AssistantEditModal({ assistant, onClose, onSaved }: {
  assistant: Assistant
  onClose: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState({
    name:           assistant.name ?? '',
    role_type:      assistant.role_type ?? 'background',
    pc_type:        (assistant as any).pc_type ?? 'unknown',
    daily_rate:     String(assistant.daily_rate ?? 12000),
    transport_cap:  String((assistant as any).transport_cap ?? 8000),
    overtime_hourly: String((assistant as any).overtime_hourly ?? 1200),
    email:          (assistant as any).email ?? '',
  })
  // 区分: 給与・シフト対象 ⇔ 日報専用（login_enabled は退職フロー側の専管のため対象外）
  const initialCategory: 'payroll' | 'report_only' =
    (assistant as any).payroll_enabled === 0 ? 'report_only' : 'payroll'
  const [category, setCategory] = useState<'payroll' | 'report_only'>(initialCategory)
  const [showCategoryConfirm, setShowCategoryConfirm] = useState(false)
  const [capabilities, setCapabilities] = useState<CapFormRow[]>(() => initCapFormRows(assistant))
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))
  const categoryChanged = category !== initialCategory

  const setCap = (idx: number, field: keyof CapFormRow, value: string | number) => {
    setCapabilities((prev) => {
      const next = [...prev]
      next[idx] = { ...next[idx], [field]: value }
      return next
    })
  }

  const doSubmit = async () => {
    setSaving(true)
    try {
      await api.assistants.update(assistant.id, {
        name:              form.name,
        role_type:         form.role_type as Assistant['role_type'],
        pc_type:           form.pc_type,
        daily_rate:        Number(form.daily_rate),
        transport_cap:     Number(form.transport_cap),
        overtime_hourly:   Number(form.overtime_hourly),
        email:             form.email || undefined,
        payroll_enabled:   category === 'payroll' ? 1 : 0,
        shift_enabled:     category === 'payroll' ? 1 : 0,
        report_enabled:    1,
        stage_capabilities: capabilities.map((c) => ({
          stage_key:           c.stage_key,
          skill_level:         c.skill_level,
          preference_order:    c.preference_order,
          capacity_multiplier: c.capacity_multiplier,
          is_active:           c.is_active,
          note:                c.note || null,
        })),
      })
      onSaved()
    } finally { setSaving(false) }
  }

  const handleSubmit = async () => {
    if (categoryChanged) { setShowCategoryConfirm(true); return }
    await doSubmit()
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal edit-modal">
        <div className="modal-header">
          <div className="modal-title">{assistant.name} の情報を編集</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {/* 基本情報 */}
          <section className="form-section">
            <div className="form-section-title">基本情報</div>
            <div className="grid-2">
              <div className="form-group">
                <label>名前</label>
                <input value={form.name} onChange={(e) => set('name', e.target.value)} />
              </div>
              <div className="form-group">
                <label>主担当（役割）</label>
                <select value={form.role_type} onChange={(e) => set('role_type', e.target.value)}>
                  <option value="background">背景</option>
                  <option value="finishing">仕上げ</option>
                  <option value="ai_art">AI作画</option>
                  <option value="research">資料</option>
                  <option value="general">全般</option>
                </select>
              </div>
              <div className="form-group">
                <label>PC種別</label>
                <select value={form.pc_type} onChange={(e) => set('pc_type', e.target.value)}>
                  <option value="unknown">未設定</option>
                  <option value="windows">Windows</option>
                  <option value="mac">Mac</option>
                </select>
                <div className="form-hint">シフト確定画面のPC設備数チェックに使用します</div>
              </div>
              <div className="form-group">
                <label>区分</label>
                <select value={category} onChange={(e) => setCategory(e.target.value as 'payroll' | 'report_only')}>
                  <option value="payroll">給与・シフト対象</option>
                  <option value="report_only">日報専用</option>
                </select>
              </div>
            </div>
            {category === 'report_only' && (
              <div className="form-hint">
                日報専用にすると、給与計算・シフト希望提出催促の対象から外れます（日報は引き続き提出できます）。
                過去の給与・勤怠・日報・シフト履歴は削除されません。
              </div>
            )}
          </section>

          {/* Phase 5-C: 対応可能作業スキル設定 */}
          <section className="form-section">
            <div className="form-section-title">対応可能作業</div>
            <div style={{ border: '1px solid var(--paper-3)', borderRadius: 6, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'var(--paper-2)' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>工程</th>
                    <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 600 }}>有効</th>
                    <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 600 }}>得意度</th>
                    <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 600 }}>優先順</th>
                    <th style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 600 }}>補正</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>メモ</th>
                  </tr>
                </thead>
                <tbody>
                  {capabilities.map((cap, idx) => {
                    const stageDef = STAGE_KEYS_DEF.find((s) => s.key === cap.stage_key)
                    return (
                      <tr key={cap.stage_key} style={{ borderTop: '1px solid var(--paper-3)', opacity: cap.is_active ? 1 : 0.45 }}>
                        <td style={{ padding: '6px 8px', fontWeight: cap.is_active ? 600 : 400 }}>
                          {stageDef?.label ?? cap.stage_key}
                        </td>
                        <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={cap.is_active === 1}
                            onChange={(e) => setCap(idx, 'is_active', e.target.checked ? 1 : 0)}
                          />
                        </td>
                        <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                          <select
                            value={cap.skill_level}
                            onChange={(e) => setCap(idx, 'skill_level', Number(e.target.value))}
                            disabled={cap.is_active === 0}
                            style={{ fontSize: 11, padding: '2px 2px', width: 52 }}
                          >
                            <option value={1}>1 苦手</option>
                            <option value={2}>2 一応</option>
                            <option value={3}>3 普通</option>
                            <option value={4}>4 得意</option>
                            <option value={5}>5 最優先</option>
                          </select>
                        </td>
                        <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                          <select
                            value={cap.preference_order}
                            onChange={(e) => setCap(idx, 'preference_order', Number(e.target.value))}
                            disabled={cap.is_active === 0}
                            style={{ fontSize: 11, padding: '2px 2px', width: 52 }}
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={30}>30</option>
                            <option value={40}>40</option>
                            <option value={50}>50</option>
                          </select>
                        </td>
                        <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                          <input
                            type="number"
                            step="0.1"
                            min="0.1"
                            max="2.0"
                            value={cap.capacity_multiplier}
                            onChange={(e) => setCap(idx, 'capacity_multiplier', Number(e.target.value))}
                            disabled={cap.is_active === 0}
                            style={{ fontSize: 11, width: 52, textAlign: 'center' }}
                          />
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          <input
                            type="text"
                            value={cap.note}
                            onChange={(e) => setCap(idx, 'note', e.target.value)}
                            disabled={cap.is_active === 0}
                            placeholder="メモ"
                            style={{ fontSize: 11, width: '100%' }}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', padding: '6px 10px', borderTop: '1px solid var(--paper-3)' }}>
                得意度: 1=苦手〜5=最優先 / 優先順: 数字が小さいほど優先 / 補正: 速い人は1.2、苦手なら0.7
              </div>
            </div>
          </section>

          {/* 給与・勤務条件 */}
          <section className="form-section">
            <div className="form-section-title">給与・勤務条件</div>
            <div className="grid-3">
              <div className="form-group">
                <label>日給（円）</label>
                <input type="number" value={form.daily_rate} onChange={(e) => set('daily_rate', e.target.value)} />
              </div>
              <div className="form-group">
                <label>残業時給（円 / 時間）</label>
                <input type="number" min={0} step={1} value={form.overtime_hourly} onChange={(e) => set('overtime_hourly', e.target.value)} />
              </div>
              <div className="form-group">
                <label>交通費上限（円）</label>
                <input type="number" value={form.transport_cap} onChange={(e) => set('transport_cap', e.target.value)} />
              </div>
            </div>
          </section>

          {/* 連絡先 */}
          <section className="form-section">
            <div className="form-section-title">連絡先</div>
            <div className="form-group">
              <label>メールアドレス</label>
              <input type="email" placeholder="example@email.com" value={form.email} onChange={(e) => set('email', e.target.value)} />
            </div>
          </section>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
      {showCategoryConfirm && (
        <ConfirmDialog
          title="区分を変更します"
          message={
            category === 'report_only'
              ? `${assistant.name} を日報専用に変更します。\n給与対象・シフト対象から外れます（シフト希望提出催促の対象にもなりません）。\n過去の給与・日報・シフト履歴は削除されません。\nよろしいですか？`
              : `${assistant.name} を給与・シフト対象に変更します。\n今後のシフト希望・給与管理の対象になります。\nよろしいですか？`
          }
          confirmLabel="変更して保存"
          confirmDanger={category === 'report_only'}
          onConfirm={async () => { setShowCategoryConfirm(false); await doSubmit() }}
          onClose={() => setShowCategoryConfirm(false)}
        />
      )}
    </div>
  )
}

function sanitizeError(msg: string): string {
  if (msg.includes('<') || msg.length > 200) {
    console.error('[MissingBanner] raw error:', msg)
    return '通知送信に失敗しました。GASの公開URLまたは権限設定を確認してください。'
  }
  return msg
}

// ================================================================
// 未提出バナー
// ================================================================
function MissingBanner({ missingData, deadline, month, onDeadlineSet, onNotified }: {
  missingData: any; deadline: any; month: string
  onDeadlineSet: () => void; onNotified: () => void
}) {
  const [deadlineInput, setDeadlineInput] = useState(deadline?.deadline_date ?? '')
  const [message, setMessage]   = useState('')
  const [saving, setSaving]     = useState(false)
  const [sending, setSending]   = useState(false)
  const [sent, setSent]         = useState<any>(null)
  const [sendError, setSendError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)

  const saveDeadline = async () => {
    if (!deadlineInput) return
    setSaving(true)
    try { await api.shiftDeadlines.set(month, deadlineInput); onDeadlineSet() }
    finally { setSaving(false) }
  }

  const sendNotification = async () => {
    setSending(true); setSent(null); setSendError(null)
    try {
      const result = await api.shiftNotifications.send(month, message || undefined)
      setSent(result); onNotified()
    } catch (ex: any) {
      setSendError(sanitizeError(ex.message ?? '送信に失敗しました'))
    } finally { setSending(false) }
  }

  const isOverdue = missingData?.is_overdue
  return (
    <div style={{
      background: isOverdue ? 'var(--red-light)' : 'var(--amber-light)',
      border: `1px solid ${isOverdue ? 'var(--red-border)' : 'var(--amber-border)'}`,
      borderRadius: 8, padding: '14px 18px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: isOverdue ? 'var(--red)' : 'var(--amber)' }}>
            {isOverdue ? '⚠ 締切超過' : '！ 未提出あり'}
          </span>
          <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
            {missingData.missing.map((a: any) => a.name).join('、')}
          </span>
          {deadline?.notified_at && (
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>最終通知: {deadline.notified_at.slice(0, 10)}</span>
          )}
        </div>
        <button className="btn btn-outline btn-sm" onClick={() => setShowForm(!showForm)}>
          {showForm ? '閉じる' : '設定・通知'}
        </button>
      </div>
      {showForm && (
        <div style={{ borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 500, minWidth: 60 }}>締切日</span>
            <input type="date" value={deadlineInput} onChange={(e) => setDeadlineInput(e.target.value)} style={{ width: 'auto', fontSize: 13 }} />
            <button className="btn btn-outline btn-sm" onClick={saveDeadline} disabled={saving || !deadlineInput}>{saving ? '…' : '保存'}</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>催促メッセージ（任意・空欄でデフォルト文面）</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="text" value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder={`${month}のシフト希望をまだ提出していません。お早めにご提出ください。`}
                style={{ flex: 1, fontSize: 13 }} />
              <button className="btn btn-primary btn-sm" onClick={sendNotification} disabled={sending}>
                {sending ? '送信中...' : `${missingData.count}名に通知`}
              </button>
            </div>
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>※ GAS_NOTIFY_URL が設定されていればメール送信、未設定ならログ記録のみ</span>
          </div>
          {sent && (
            <div style={{ background: 'var(--green-light)', border: '1px solid rgba(45,122,58,0.3)', borderRadius: 6, padding: '10px 14px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>✓</span>
              <div>
                <div style={{ fontWeight: 600, color: 'var(--green)' }}>
                  {sent.sent > 0 ? `${sent.sent}名に催促を送信しました` : '送信完了（未提出者なし）'}
                </div>
                {sent.names?.length > 0 && <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>送信先: {sent.names.join('、')}</div>}
              </div>
            </div>
          )}
          {sendError && (
            <div style={{ background: 'var(--red-light)', border: '1px solid rgba(192,57,43,0.25)', borderRadius: 6, padding: '10px 14px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: 'var(--red)', fontSize: 16 }}>✗</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: 'var(--red)' }}>送信に失敗しました</div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{sendError}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ================================================================
// 勤怠入力モーダル
// ================================================================
function AttendanceModal({ assistants, defaultDate, onClose, onSaved, initialValues }: {
  assistants: Assistant[]
  defaultDate: string
  onClose: () => void
  onSaved: (id: string, name: string, date: string) => void
  initialValues?: {
    id?: string; assistant_id?: string; work_date?: string
    check_in?: string; check_out?: string; overtime_min?: number
    transport_fee?: number; meal_provided?: number
    work_type?: 'full_time' | 'part_time'; work_minutes?: number; break_minutes?: number
    notes?: string
  }
}) {
  const isEdit = !!initialValues?.id
  const [form, setForm] = useState({
    assistant_id:  initialValues?.assistant_id  ?? assistants[0]?.id ?? '',
    work_date:     initialValues?.work_date     ?? defaultDate,
    check_in:      initialValues?.check_in      ?? '10:00',
    check_out:     initialValues?.check_out     ?? '20:00',
    work_type:     initialValues?.work_type === 'part_time' ? 'part_time' : 'full_time',
    work_minutes:  String(initialValues?.work_minutes ?? 0),
    break_minutes: String(initialValues?.break_minutes ?? BREAK_MINUTES),
    overtime_min:  String(initialValues?.overtime_min  ?? 0),
    transport_fee: String(initialValues?.transport_fee ?? 0),
    meal_provided: String(initialValues?.meal_provided ?? 1),
  })
  const [saving, setSaving]   = useState(false)
  const [errors, setErrors]   = useState<Record<string, string>>({})
  const [closedMonth, setClosedMonth] = useState<string | null>(null)
  const [isEditingBreak, setIsEditingBreak] = useState(false)
  const set = (k: string, v: string) => {
    setForm((f) => ({ ...f, [k]: v }))
    setErrors((e) => { const n = { ...e }; delete n[k]; return n })
  }

  useEffect(() => {
    const month = form.work_date?.slice(0, 7)
    if (!month) return
    api.monthlyCloses.get(month)
      .then((cl: any) => setClosedMonth(cl?.status === 'closed' ? month : null))
      .catch(() => setClosedMonth(null))
  }, [form.work_date])

  // ★ 出勤時刻・退勤時刻・休憩時間・勤務区分の変更時に、残業時間（または勤務時間）を自動再計算する。
  // 手動の「残業自動計算」「時刻から計算」ボタンは廃止し、自動計算に一本化した。
  // （保存時はサーバー側でも同じ考え方で必ず再計算されるため、二重に安全）
  const recalcDerivedFields = (next: typeof form): typeof form => {
    if (next.work_type === 'part_time') {
      const minutes = calcPartTimeWorkMinutes(next.check_in, next.check_out, Number(next.break_minutes) || 0)
      return { ...next, work_minutes: String(minutes), overtime_min: '0' }
    }
    const fullTimeBreakMin = Number(next.break_minutes) || 0
    return { ...next, overtime_min: String(calcFullTimeOvertimeMinutes(next.check_in, next.check_out, fullTimeBreakMin)) }
  }

  const updateTime = (key: 'check_in' | 'check_out', value: string) => {
    setForm((f) => recalcDerivedFields({ ...f, [key]: value }))
    setErrors((e) => { const n = { ...e }; delete n[key]; return n })
  }

  const updateWorkType = (value: 'full_time' | 'part_time') => {
    setForm((f) => recalcDerivedFields({ ...f, work_type: value }))
  }

  const updateBreak = (value: string) => {
    setForm((f) => recalcDerivedFields({ ...f, break_minutes: value }))
  }

  const preview = (() => {
    const asst = assistants.find((a) => a.id === form.assistant_id)
    if (!asst) return 0
    const normalized = normalizeAttendanceFormForSubmit(form)
    const overtime  = Math.round((normalized.overtimeMin / 60) * asst.overtime_hourly)
    const transport = Math.min(Number(form.transport_fee), asst.transport_cap)
    const basePay   = form.work_type === 'part_time'
      ? Math.round((normalized.workMinutes / 60) * asst.overtime_hourly)
      : asst.daily_rate
    return basePay + overtime + transport
  })()

  const handleSubmit = async () => {
    const e: Record<string, string> = {}
    if (!form.assistant_id) e.assistant_id = 'アシスタントを選択してください'
    if (!form.work_date)    e.work_date = '日付を入力してください'
    if (form.work_type !== 'part_time') {
      const breakVal = form.break_minutes.trim()
      const breakNum = Number(breakVal)
      if (breakVal === '' || !Number.isFinite(breakNum) || breakNum < 0 || breakNum > BREAK_MINUTES_MAX) {
        e.break_minutes = `休憩時間は0〜${BREAK_MINUTES_MAX}分の範囲で入力してください`
      }
    }
    if (Object.keys(e).length > 0) { setErrors(e); return }
    setSaving(true)
    try {
      const normalized = normalizeAttendanceFormForSubmit(form)
      const result: any = await api.attendance.record({
        assistant_id:  form.assistant_id, work_date: form.work_date,
        check_in: form.check_in, check_out: form.check_out,
        overtime_min: normalized.overtimeMin, transport_fee: Number(form.transport_fee),
        meal_provided: Number(form.meal_provided),
        work_type: form.work_type as 'full_time' | 'part_time',
        work_minutes: normalized.workMinutes,
        break_minutes: normalized.breakMinutes,
      })
      const asst = assistants.find((a) => a.id === form.assistant_id)
      onSaved(result?.id ?? '', asst?.name ?? '', form.work_date)
    } catch (ex: any) {
      setErrors({ submit: ex.message ?? 'エラーが発生しました' })
    } finally { setSaving(false) }
  }

  return (
    // ★ 入力途中の内容が消えないよう、背景クリックでは閉じない。
    //   閉じられるのは ✕ ボタン・キャンセルボタン・保存成功時のみ。
    <div className="overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isEdit ? '勤怠を修正' : '勤怠入力'}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {closedMonth && (
            <div style={{ background: 'var(--blue-light)', border: '1px solid rgba(30,95,168,0.25)', borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>
              🔒 {closedMonth} は締め済みのため保存できません
            </div>
          )}
          <div className="grid-2">
            <div className="form-group">
              <label>アシスタント <span style={{ color: 'var(--red)' }}>*</span></label>
              {isEdit
                ? <input value={assistants.find((a) => a.id === form.assistant_id)?.name ?? ''} disabled />
                : <select value={form.assistant_id} onChange={(e) => set('assistant_id', e.target.value)}>
                    {assistants.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
              }
              {errors.assistant_id && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.assistant_id}</div>}
            </div>
            <div className="form-group">
              <label>日付 <span style={{ color: 'var(--red)' }}>*</span></label>
              <input type="date" value={form.work_date}
                onChange={(e) => !isEdit && set('work_date', e.target.value)}
                readOnly={isEdit}
                style={{ borderColor: errors.work_date ? 'var(--red)' : undefined, background: isEdit ? 'var(--paper-2)' : undefined }} />
              {errors.work_date && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.work_date}</div>}
            </div>
          </div>
          <div className="form-group">
            <label>勤務区分</label>
            <select value={form.work_type} onChange={(e) => updateWorkType(e.target.value as 'full_time' | 'part_time')}>
              <option value="full_time">フルタイム（日給）</option>
              <option value="part_time">時間勤務・時短勤務</option>
            </select>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3 }}>
              早退・時短勤務の場合は「時間勤務」を選び、勤務時間を分で入力してください
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label>出勤時刻</label>
              <input type="time" value={form.check_in} onChange={(e) => updateTime('check_in', e.target.value)} />
            </div>
            <div className="form-group">
              <label>退勤時刻</label>
              <input type="time" value={form.check_out} onChange={(e) => updateTime('check_out', e.target.value)} />
            </div>
          </div>

          {form.work_type === 'part_time' && (
            <div className="form-group">
              <label>休憩時間（分）</label>
              <select value={form.break_minutes} onChange={(e) => updateBreak(e.target.value)}>
                <option value="0">0分（休憩なし）</option>
                <option value="30">30分</option>
                <option value="60">60分</option>
                <option value="90">90分</option>
              </select>
            </div>
          )}

          {form.work_type !== 'part_time' ? (
            <>
            <div className="form-group">
              <label>休憩時間</label>
              {isEditingBreak ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="number" min={0} max={BREAK_MINUTES_MAX} step={1}
                    value={form.break_minutes}
                    onChange={(e) => updateBreak(e.target.value)}
                    style={{ width: 90 }} />
                  <span style={{ fontSize: 13 }}>分</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 15 }}>
                    {formatBreakMinutes(Number(form.break_minutes) || 0)}
                  </span>
                  <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                    onClick={() => setIsEditingBreak(true)}>休憩時間を変更</button>
                </div>
              )}
              {errors.break_minutes && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.break_minutes}</div>}
            </div>
            <div className="form-group">
              <label>残業時間</label>
              <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 15 }}>
                {form.overtime_min}分
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3 }}>
                実勤務 {(Math.max(0, diffMinutes(form.check_in, form.check_out) - (Number(form.break_minutes) || 0)) / 60).toFixed(1)}時間／
                残業 {(Number(form.overtime_min) / 60).toFixed(1)}時間
                （実勤務が{(NORMAL_PAID_MINUTES / 60).toFixed(1)}時間を超えた分を自動計算）
              </div>
            </div>
            </>
          ) : (
            <div className="form-group">
              <label>勤務時間（分）</label>
              <input type="number" value={form.work_minutes} onChange={(e) => set('work_minutes', e.target.value)} />
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3 }}>
                出退勤時刻と休憩時間から自動計算されます。時間勤務・時短勤務では残業は計上しません。
                {Number(form.work_minutes) > 0 && (
                  <> ≈ {(Number(form.work_minutes) / 60).toFixed(1)}時間 ／ 概算 ¥{fmt(Math.round((Number(form.work_minutes) / 60) * (assistants.find((a) => a.id === form.assistant_id)?.overtime_hourly ?? 1200)))}</>
                )}
              </div>
            </div>
          )}

          <div className="form-group">
            <label>交通費（円）</label>
            <input type="number" value={form.transport_fee} onChange={(e) => set('transport_fee', e.target.value)} />
          </div>
          <div className="form-group">
            <label>食事支給<span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 6, fontWeight: 400 }}>※ 会社直接負担のため振込額には含まれません（内部管理用）</span></label>
            <select value={form.meal_provided} onChange={(e) => set('meal_provided', e.target.value)}>
              <option value="1">あり</option>
              <option value="0">なし</option>
            </select>
          </div>
          <div style={{ background: 'var(--paper-2)', borderRadius: 6, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>振込予定額（参考）</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 15 }}>¥{preview.toLocaleString()}</span>
          </div>
          {errors.submit && (
            <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8, padding: '8px 14px', background: 'var(--red-light)', borderRadius: 6 }}>
              ⚠ {errors.submit}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving || !!closedMonth}>
            {saving ? '保存中...' : isEdit ? '修正を保存' : '記録'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ================================================================
// 月次支払パネル
// ================================================================
function PaymentPanel({ month, data, onRefresh }: { month: string; data: any; onRefresh: () => void }) {
  const isMobile = useIsMobile()
  const [generating, setGenerating] = useState(false)
  const [reviewing,  setReviewing]  = useState(false)
  const [approving,  setApproving]  = useState(false)
  const [bulkPaying, setBulkPaying] = useState(false)
  const [closeData,  setCloseData]  = useState<any>(null)
  const [reopenNote, setReopenNote] = useState('')
  const [showReopen, setShowReopen] = useState(false)
  const [reopening,  setReopening]  = useState(false)
  const [toast,      setToast]      = useState<{ ok: boolean; msg: string } | null>(null)
  const [confirmOp,  setConfirmOp]  = useState<{ type: string; id?: string; name?: string } | null>(null)
  const [bonusInitial, setBonusInitial] = useState<BonusEditRecord | null>(null)
  const [showBonusModal, setShowBonusModal] = useState(false)

  const showToast = (ok: boolean, msg: string) => { setToast({ ok, msg }); setTimeout(() => setToast(null), 5000) }

  useEffect(() => {
    api.monthlyCloses.get(month).then(setCloseData).catch(() => setCloseData({ status: 'open' }))
  }, [month, data])

  const closeStatus: string = closeData?.status ?? 'open'
  const isClosed = closeStatus === 'closed'

  const generate    = async () => {
    setGenerating(true)
    try {
      const res = await api.monthlyPayments.generate(month)
      onRefresh()
      const applied = res.claims_applied ?? 0
      showToast(true, applied > 0
        ? `勤怠から再集計しました（更新：${res.updated}名／うち${applied}名は確定済み給与申告額を優先反映）`
        : `勤怠から再集計しました（更新：${res.updated}名）`
      )
    } catch (e: any) { showToast(false, e.message) }
    finally { setGenerating(false) }
  }
  const generateWithClaims = async () => {
    setGenerating(true)
    try {
      const res = await api.monthlyPayments.generate(month, { use_confirmed_claims: true })
      onRefresh()
      const applied = res.claims_applied ?? 0
      showToast(true, applied > 0
        ? `申告確定額を反映して集計しました（${applied}名に確定額を適用）`
        : '集計しました（確定申告の対象なし、通常の勤怠集計を使用）'
      )
    } catch (e: any) { showToast(false, e.message) }
    finally { setGenerating(false) }
  }
  const bulkReview  = async () => { setReviewing(true);   try { await api.monthlyPayments.bulkReview(month); await api.monthlyCloses.review(month); onRefresh(); showToast(true, 'レビュー開始しました') } catch (e: any) { showToast(false, e.message) } finally { setReviewing(false) } }
  const approve     = async () => { setApproving(true);   try { await api.monthlyCloses.approve(month); onRefresh(); showToast(true, `${month} を承認・締め処理しました`) } catch (e: any) { showToast(false, e.message) } finally { setApproving(false) } }
  const bulkPaid    = async () => { setBulkPaying(true);  try { const res: any = await api.monthlyPayments.bulkPaid(month); onRefresh(); showToast(true, `${res.updated}名を支払済にしました`) } catch (e: any) { showToast(false, e.message) } finally { setBulkPaying(false) } }
  const reopen      = async () => { if (!reopenNote.trim()) { showToast(false, '解除理由を入力してください'); return } setReopening(true); try { await api.monthlyCloses.reopen(month, reopenNote); setShowReopen(false); setReopenNote(''); onRefresh(); showToast(true, '締めを解除しました') } catch (e: any) { showToast(false, e.message) } finally { setReopening(false) } }
  const markPaid    = async (id: string) => { try { await api.monthlyPayments.updateStatus(id, 'paid'); onRefresh(); showToast(true, '支払済にしました') } catch (e: any) { showToast(false, e.message) } }
  const revertPaid  = async (id: string) => { try { await api.monthlyPayments.updateStatus(id, 'approved'); onRefresh(); showToast(true, '承認済に戻しました') } catch (e: any) { showToast(false, e.message) } }
  const markReviewed   = async (id: string) => { try { await api.monthlyPayments.updateStatus(id, 'reviewed'); onRefresh() } catch (e: any) { showToast(false, e.message) } }
  const revertReviewed = async (id: string) => { try { await api.monthlyPayments.updateStatus(id, 'draft'); onRefresh(); showToast(true, '未確認に戻しました') } catch (e: any) { showToast(false, e.message) } }

  const payments: any[] = data?.payments ?? []
  const live: any[]     = data?.live ?? []
  const summary         = data?.summary ?? {}
  const draftCount    = payments.filter((p: any) => p.status === 'draft').length
  const reviewedCount = payments.filter((p: any) => p.status === 'reviewed').length
  const approvedCount = payments.filter((p: any) => p.status === 'approved').length
  const paidCount     = payments.filter((p: any) => p.status === 'paid').length
  const canApprove = closeStatus === 'reviewing' && draftCount === 0
  const canReview  = !isClosed && payments.length > 0 && draftCount > 0
  const statusBadge = (s: string) => {
    if (s === 'paid')     return <span className="badge badge-green">支払済</span>
    if (s === 'approved') return <span className="badge badge-blue">承認済</span>
    if (s === 'reviewed') return <span className="badge" style={{ background: 'var(--blue-light)', color: 'var(--blue)' }}>レビュー済</span>
    return <span className="badge badge-gray">未確認</span>
  }

  return (
    <div>
      {toast && <Toast msg={toast.msg} ok={toast.ok} onClose={() => setToast(null)} />}
      {showBonusModal && (
        <BonusEditModal
          initial={bonusInitial}
          assistants={payments.map((p: any) => ({ id: p.assistant_id, name: p.assistant_name }))}
          onClose={() => { setShowBonusModal(false); setBonusInitial(null) }}
          onSaved={() => showToast(true, '賞与を登録しました')}
        />
      )}
      {confirmOp?.type === 'approve' && <ConfirmDialog title="承認・締め処理" message={`${month} を承認・締めします。\n締め後は固定費の支払状態変更がロックされます。\n\nよろしいですか？`} confirmLabel="承認・締める" onConfirm={async () => { await approve(); setConfirmOp(null) }} onClose={() => setConfirmOp(null)} />}
      {confirmOp?.type === 'bulk-paid' && <ConfirmDialog title="一括支払済" message={`承認済の${approvedCount}名を全員支払済にします。\n\nよろしいですか？`} confirmLabel="一括支払済にする" onConfirm={async () => { await bulkPaid(); setConfirmOp(null) }} onClose={() => setConfirmOp(null)} />}
      {confirmOp?.type === 'mark-paid' && confirmOp.id && <ConfirmDialog title="支払済にする" message={`${confirmOp.name} を支払済にします。\nよろしいですか？`} confirmLabel="支払済にする" onConfirm={async () => { await markPaid(confirmOp.id!); setConfirmOp(null) }} onClose={() => setConfirmOp(null)} />}
      {confirmOp?.type === 'revert-paid' && confirmOp.id && <ConfirmDialog title="承認済に戻す" message={`${confirmOp.name} を「承認済」に戻します。\nよろしいですか？`} confirmLabel="承認済に戻す" onConfirm={async () => { await revertPaid(confirmOp.id!); setConfirmOp(null) }} onClose={() => setConfirmOp(null)} />}
      {confirmOp?.type === 'generate-with-claims' && (
        <ConfirmDialog
          title="申告確定額を反映して再集計"
          message={`「給与申告確認」タブで「確定」済み かつ 確定額が設定されているアシスタントのみ、その金額を monthly_payments に反映します。\n\n確定額が未設定のアシスタントは通常の勤怠集計を使用します。\n\n実行しますか？`}
          confirmLabel="反映して再集計する"
          onConfirm={async () => { setConfirmOp(null); await generateWithClaims() }}
          onClose={() => setConfirmOp(null)}
        />
      )}

      {isClosed && (
        <div style={{ background: 'var(--blue-light)', border: '1px solid rgba(30,95,168,0.2)', borderRadius: 8, padding: '12px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <span style={{ fontWeight: 700, color: 'var(--blue)', fontSize: 13 }}>🔒 {month} は締め済みです</span>
            {closeData?.closed_at && <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 10 }}>{closeData.closed_at.slice(0, 10)}</span>}
          </div>
          <button className="btn btn-outline btn-sm" onClick={() => setShowReopen(!showReopen)}>締めを解除</button>
        </div>
      )}
      {showReopen && (
        <div style={{ background: 'var(--paper-2)', border: '1px solid var(--paper-3)', borderRadius: 8, padding: '14px 18px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
          <input type="text" placeholder="解除理由を入力（必須）" value={reopenNote} onChange={(e) => setReopenNote(e.target.value)} style={{ flex: 1, fontSize: 13 }} />
          <button className="btn btn-danger btn-sm" onClick={reopen} disabled={reopening}>{reopening ? '…' : '解除する'}</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowReopen(false)}>キャンセル</button>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div className="metric" style={{ minWidth: 110, padding: '10px 14px' }}>
            <div className="metric-label">支払合計</div>
            <div className="metric-value">{payments.length > 0 ? `¥${fmt(summary.total_amount)}` : '—'}</div>
          </div>
          <div className="metric" style={{ minWidth: 80, padding: '10px 14px' }}>
            <div className="metric-label">未確認</div>
            <div className={`metric-value ${draftCount > 0 ? 'amber' : 'green'}`}>{draftCount}名</div>
          </div>
          <div className="metric" style={{ minWidth: 80, padding: '10px 14px' }}>
            <div className="metric-label">レビュー済</div>
            <div className="metric-value">{reviewedCount}名</div>
          </div>
          <div className="metric" style={{ minWidth: 80, padding: '10px 14px' }}>
            <div className="metric-label">支払済</div>
            <div className={`metric-value ${paidCount === payments.length && payments.length > 0 ? 'green' : ''}`}>{paidCount}/{payments.length}名</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!isClosed && <button className="btn btn-outline btn-sm" onClick={generate} disabled={generating}>{generating ? '集計中...' : '勤怠から再集計'}</button>}
          {!isClosed && (
            <button className="btn btn-outline btn-sm"
              style={{ color: 'var(--amber)', borderColor: 'var(--amber)' }}
              onClick={() => setConfirmOp({ type: 'generate-with-claims' })}
              disabled={generating}
              title="給与申告確認タブで「確定」+確定額が設定されているアシスタントのみ、その金額を反映して再集計します">
              申告確定額を反映して再集計
            </button>
          )}
          {canReview && <button className="btn btn-outline btn-sm" onClick={bulkReview} disabled={reviewing}>{reviewing ? '…' : `未確認${draftCount}件をレビュー済にする`}</button>}
          {!isClosed && closeStatus === 'reviewing' && <button className="btn btn-primary btn-sm" onClick={() => setConfirmOp({ type: 'approve' })} disabled={approving || !canApprove}>承認・締め処理</button>}
          {!isClosed && closeStatus === 'open' && payments.length > 0 && draftCount === 0 && <button className="btn btn-primary btn-sm" onClick={bulkReview} disabled={reviewing}>{reviewing ? '…' : 'レビュー開始'}</button>}
          {approvedCount > 0 && <button className="btn btn-primary btn-sm" onClick={() => setConfirmOp({ type: 'bulk-paid' })} disabled={bulkPaying} style={{ background: 'var(--green)' }}>承認済{approvedCount}名を一括支払済</button>}
          {payments.length > 0 && <>
            <a href={api.monthlyPayments.csvUrl(month)} className="btn btn-outline btn-sm" download>支払集計CSV</a>
            <a href={api.export.paymentDetailUrl(month)} className="btn btn-outline btn-sm" download>支払明細CSV</a>
            <a href={api.export.yayoiUrl(month, false)} className="btn btn-outline btn-sm" download>やよい仕訳CSV</a>
            <a href={api.export.expenseSummaryUrl(month)} className="btn btn-outline btn-sm" download>やよい経費CSV</a>
          </>}
        </div>
      </div>

      {payments.length === 0 && live.length > 0 && (
        <div style={{ background: 'var(--paper-2)', border: '1px dashed var(--paper-3)', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 8 }}>⬆ 「勤怠から再集計」ボタンを押すと、以下の内容で支払レコードが作成されます</div>
          <div style={{ fontSize: 13 }}>勤怠登録済み: {live.filter((r: any) => r.days_worked > 0).length}名 / 合計 ¥{fmt(live.reduce((s: number, r: any) => s + r.total_amount, 0))}</div>
        </div>
      )}

      {payments.length > 0 ? (
        <div className="card">
          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 14px' }}>
              {payments.map((p: any) => {
                const transferAmount = p.total_amount ?? 0
                const hasClaim = p.claim_amount != null
                return (
                  <div key={p.id} className="card" style={{
                    padding: '14px 16px',
                    background: p.status === 'paid' ? 'var(--paper-2)' : hasClaim ? 'rgba(45,122,58,0.03)' : undefined,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 6 }}>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>
                        {p.assistant_name}
                        {hasClaim && (
                          <span style={{
                            fontSize: 9, marginLeft: 5,
                            background: 'var(--green-light)', color: 'var(--green)',
                            border: '1px solid rgba(45,122,58,0.2)',
                            borderRadius: 3, padding: '1px 4px', verticalAlign: 'middle',
                          }}>申告額</span>
                        )}
                      </div>
                      {statusBadge(p.status)}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: 6, columnGap: 8, marginTop: 10, fontSize: 13 }}>
                      <div style={{ color: 'var(--ink-3)' }}>出勤日数</div>
                      <div className="mono">
                        {p.days_worked}日
                        {(p.part_time_total_min ?? 0) > 0 && (
                          <span style={{ fontSize: 11, color: 'var(--blue)', marginLeft: 4 }}>
                            / {((p.part_time_total_min ?? 0) / 60).toFixed(1)}h
                          </span>
                        )}
                      </div>
                      <div style={{ color: 'var(--ink-3)' }}>基本給</div>
                      <div className="mono">¥{fmt(p.base_pay)}</div>
                      <div style={{ color: 'var(--ink-3)' }}>残業代</div>
                      <div className="mono" style={{ color: p.overtime_pay > 0 ? 'var(--amber)' : undefined }}>{p.overtime_pay > 0 ? `¥${fmt(p.overtime_pay)}` : '―'}</div>
                      <div style={{ color: 'var(--ink-3)' }}>交通費</div>
                      <div className="mono">¥{fmt(p.transport_total)}</div>
                      <div style={{ color: 'var(--ink-3)' }}>その他経費</div>
                      <div className="mono">{(p.other_total ?? 0) > 0 ? `¥${fmt(p.other_total)}` : '―'}</div>
                      <div style={{ color: 'var(--ink-3)' }}>食費（参考）</div>
                      <div className="mono" style={{ color: 'var(--ink-3)', fontSize: 12 }}>{p.meal_total > 0 ? `¥${fmt(p.meal_total)}` : '―'}</div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--paper-3)' }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>振込合計</span>
                      <span className="mono" style={{ fontSize: 17, fontWeight: 700, color: 'var(--green)' }}>¥{fmt(transferAmount)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
                      {!isClosed && p.status === 'draft' && <button className="btn btn-sm btn-outline" onClick={() => markReviewed(p.id)}>レビュー済</button>}
                      {!isClosed && p.status === 'reviewed' && <button className="btn btn-sm btn-outline" style={{ color: 'var(--ink-3)' }} onClick={() => revertReviewed(p.id)}>↩ 未確認に戻す</button>}
                      {p.status === 'approved' && <button className="btn btn-sm btn-primary" onClick={() => setConfirmOp({ type: 'mark-paid', id: p.id, name: p.assistant_name })}>支払済</button>}
                      {p.status === 'paid' && <button className="btn btn-sm btn-outline" style={{ color: 'var(--ink-3)' }} onClick={() => setConfirmOp({ type: 'revert-paid', id: p.id, name: p.assistant_name })}>↩ 戻す</button>}
                      <button className="btn btn-sm btn-outline"
                        title="このアシスタントの賞与を登録します（通常給与とは別管理）"
                        onClick={() => { setBonusInitial({ assistant_id: p.assistant_id }); setShowBonusModal(true) }}>
                        ＋賞与を追加
                      </button>
                    </div>
                  </div>
                )
              })}
              <div className="card" style={{ padding: '14px 16px', background: 'var(--paper-2)' }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>合計</div>
                <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: 6, columnGap: 8, fontSize: 13 }}>
                  <div style={{ color: 'var(--ink-3)' }}>出勤日数</div>
                  <div className="mono">{payments.reduce((s: number, p: any) => s + p.days_worked, 0)}日</div>
                  <div style={{ color: 'var(--ink-3)' }}>基本給</div>
                  <div className="mono">¥{fmt(payments.reduce((s: number, p: any) => s + p.base_pay, 0))}</div>
                  <div style={{ color: 'var(--ink-3)' }}>残業代</div>
                  <div className="mono">¥{fmt(payments.reduce((s: number, p: any) => s + p.overtime_pay, 0))}</div>
                  <div style={{ color: 'var(--ink-3)' }}>交通費</div>
                  <div className="mono">¥{fmt(payments.reduce((s: number, p: any) => s + p.transport_total, 0))}</div>
                  <div style={{ color: 'var(--ink-3)' }}>その他経費</div>
                  <div className="mono">¥{fmt(payments.reduce((s: number, p: any) => s + (p.other_total ?? 0), 0))}</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--paper-3)' }}>
                  <span style={{ fontWeight: 700 }}>振込合計</span>
                  <span className="mono" style={{ fontSize: 17, fontWeight: 700 }}>¥{fmt(payments.reduce((s: number, p: any) => s + (p.total_amount ?? 0), 0))}</span>
                </div>
              </div>
            </div>
          ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>名前</th><th>出勤日</th>
                  <th style={{ textAlign: 'right' }}>基本給</th>
                  <th style={{ textAlign: 'right' }}>残業代</th>
                  <th style={{ textAlign: 'right' }}>交通費</th>
                  <th style={{ textAlign: 'right' }}>その他経費</th>
                  <th style={{ textAlign: 'right', color: 'var(--ink-3)', fontWeight: 400, fontSize: 11 }}>食費<br/>(参考)</th>
                  <th style={{ textAlign: 'right', fontWeight: 700 }}>振込合計</th>
                  <th>状態</th><th></th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p: any) => {
                  // total_amount は generate 時点で食費除外済み（daily_total の合計）
                  // meal_total は会社直接負担の参考値のみ。ここで引いてはいけない。
                  const transferAmount = p.total_amount ?? 0
                  const hasClaim = p.claim_amount != null
                  return (
                    <tr key={p.id} style={{ background: p.status === 'paid' ? 'var(--paper-2)' : hasClaim ? 'rgba(45,122,58,0.03)' : undefined }}>
                      <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                        {p.assistant_name}
                        {hasClaim && (
                          <span style={{
                            fontSize: 9, marginLeft: 5,
                            background: 'var(--green-light)', color: 'var(--green)',
                            border: '1px solid rgba(45,122,58,0.2)',
                            borderRadius: 3, padding: '1px 4px', verticalAlign: 'middle',
                          }}>申告額</span>
                        )}
                      </td>
                      <td className="mono">
                        {p.days_worked}日
                        {(p.part_time_total_min ?? 0) > 0 && (
                          <span style={{ fontSize: 11, color: 'var(--blue)', marginLeft: 4 }}>
                            / {((p.part_time_total_min ?? 0) / 60).toFixed(1)}h
                          </span>
                        )}
                      </td>
                      <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(p.base_pay)}</td>
                      <td className="mono" style={{ textAlign: 'right', color: p.overtime_pay > 0 ? 'var(--amber)' : 'var(--ink-3)' }}>{p.overtime_pay > 0 ? `¥${fmt(p.overtime_pay)}` : '—'}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(p.transport_total)}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>{(p.other_total ?? 0) > 0 ? `¥${fmt(p.other_total)}` : '—'}</td>
                      <td className="mono" style={{ textAlign: 'right', color: 'var(--ink-3)', fontSize: 12 }}>{p.meal_total > 0 ? `¥${fmt(p.meal_total)}` : '—'}</td>
                      <td className="mono" style={{ textAlign: 'right', fontWeight: 700, fontSize: 14 }}>¥{fmt(transferAmount)}</td>
                      <td>{statusBadge(p.status)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {!isClosed && p.status === 'draft' && <button className="btn btn-sm btn-outline" onClick={() => markReviewed(p.id)} style={{ fontSize: 11 }}>レビュー済</button>}
                        {!isClosed && p.status === 'reviewed' && <button className="btn btn-sm btn-ghost" onClick={() => revertReviewed(p.id)} style={{ fontSize: 10, color: 'var(--ink-3)' }}>↩ 未確認に戻す</button>}
                        {p.status === 'approved' && <button className="btn btn-sm btn-primary" onClick={() => setConfirmOp({ type: 'mark-paid', id: p.id, name: p.assistant_name })} style={{ fontSize: 11 }}>支払済</button>}
                        {p.status === 'paid' && <button className="btn btn-sm btn-ghost" onClick={() => setConfirmOp({ type: 'revert-paid', id: p.id, name: p.assistant_name })} style={{ fontSize: 10, color: 'var(--ink-3)' }}>↩ 戻す</button>}
                        <button className="btn btn-sm btn-outline" style={{ fontSize: 11, marginLeft: 4 }}
                          title="このアシスタントの賞与を登録します（通常給与とは別管理）"
                          onClick={() => { setBonusInitial({ assistant_id: p.assistant_id }); setShowBonusModal(true) }}>
                          ＋賞与を追加
                        </button>
                      </td>
                    </tr>
                  )
                })}
                <tr style={{ background: 'var(--paper-2)', fontWeight: 700 }}>
                  <td>合計</td>
                  <td className="mono">{payments.reduce((s: number, p: any) => s + p.days_worked, 0)}日</td>
                  <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(payments.reduce((s: number, p: any) => s + p.base_pay, 0))}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(payments.reduce((s: number, p: any) => s + p.overtime_pay, 0))}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(payments.reduce((s: number, p: any) => s + p.transport_total, 0))}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(payments.reduce((s: number, p: any) => s + (p.other_total ?? 0), 0))}</td>
                  <td className="mono" style={{ textAlign: 'right', color: 'var(--ink-3)', fontSize: 12 }}>¥{fmt(payments.reduce((s: number, p: any) => s + (p.meal_total ?? 0), 0))}</td>
                  <td className="mono" style={{ textAlign: 'right', fontSize: 15 }}>¥{fmt(payments.reduce((s: number, p: any) => s + (p.total_amount ?? 0), 0))}</td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          </div>
          )}
          <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--ink-3)', borderTop: '1px solid var(--paper-2)' }}>
            ※ 食費（参考列）は会社がモバイルオーダーで直接負担するため振込合計には含まれません
          </div>
        </div>
      ) : (
        <div className="loading" style={{ padding: 48 }}>勤怠データを集計して支払レコードを作成してください</div>
      )}
    </div>
  )
}

// ================================================================
// 確定シフトパネル（週表示切り替え付き）
// ================================================================
const DOW_LABEL = ['日', '月', '火', '水', '木', '金', '土']

function getDow(dateStr: string): number {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d).getDay()
}

type ConfirmedShiftAsst = {
  assistant_id: string
  assistant_name: string
  role_type: string
  pc_type?: string | null
  daily_rate: number
  request_id: string | null
  available_dates: string | null
  shift_id: string | null
  confirmed_dates: string | null
  confirmed_note: string | null
  shift_status: string | null
}

type ShiftMonthData = {
  month: string
  days_in_month: number
  assistants: ConfirmedShiftAsst[]
}

// PC設備数の上限判定: 日付ごとの Windows/Mac 使用台数と、上限に対する状態。
// PC種別未設定のアシスタントはどちらのカウントにも含めない（hasUnset で有無だけ分かる）。
type PcStatus = 'ok' | 'full' | 'over'
type PcCapacityStatus = {
  date: string
  macUsed: number
  macLimit: number
  windowsUsed: number
  windowsLimit: number
  macStatus: PcStatus
  windowsStatus: PcStatus
  hasUnset: boolean
}
function pcStatusOf(used: number, limit: number): PcStatus {
  if (limit <= 0) return used > 0 ? 'over' : 'ok'
  if (used > limit) return 'over'
  if (used === limit) return 'full'
  return 'ok'
}
function pcStatusColor(status: PcStatus): string {
  if (status === 'over') return 'var(--red)'
  if (status === 'full') return 'var(--amber)'
  return 'var(--ink-3)'
}

// ================================================================
// PC台数設定 編集モーダル（控えめな導線用）
// 制作環境設定ページ（ProductionPlanningSettings.tsx）と同じ
// production_resource_settings / GET・PUT /production-planning/resource-settings
// を使う。新しいテーブル・二重管理は一切なし。is_enabled/notes は変更せずそのまま送る。
// ================================================================
function PcCapacityEditModal({
  current, onClose, onSaved,
}: {
  current: ResourceSettings
  onClose: () => void
  onSaved: (updated: ResourceSettings) => void
}) {
  const [windows, setWindows] = useState(String(current.windows_capacity))
  const [mac, setMac] = useState(String(current.mac_capacity))
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const windowsNum = windows === '' ? NaN : Number(windows)
  const macNum = mac === '' ? NaN : Number(mac)
  const valid =
    Number.isInteger(windowsNum) && windowsNum >= 0 && windowsNum <= 50 &&
    Number.isInteger(macNum) && macNum >= 0 && macNum <= 50

  const handleSave = async () => {
    if (!valid) { setErr('0〜50の整数で入力してください'); return }
    setSaving(true)
    setErr(null)
    try {
      const res = await api.productionPlanning.updateResourceSettings({
        windows_capacity: windowsNum,
        mac_capacity: macNum,
        is_enabled: current.is_enabled,
        notes: current.notes,
      })
      onSaved(res.resource_settings)
      onClose()
    } catch (e: any) {
      setErr(e.message ?? '保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 320 }}>
        <div className="modal-header">
          <div className="modal-title">PC台数設定</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 12 }}>
            制作環境設定と共通の値です。ここで変更すると制作環境設定ページにも反映されます。
          </div>
          {err && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 8 }}>{err}</div>}
          <div className="form-group">
            <label>Windows作業可能人数</label>
            <input type="number" min={0} max={50} value={windows}
              onChange={(e) => setWindows(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Mac作業可能人数</label>
            <input type="number" min={0} max={50} value={mac}
              onChange={(e) => setMac(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !valid}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ConfirmedShiftPanel({ month }: { month: string }) {
  const [data, setData]             = useState<ShiftMonthData | null>(null)
  const [loading, setLoading]       = useState(true)
  const [localDates, setLocalDates] = useState<Record<string, string[]>>({})
  const [dirty, setDirty]           = useState<Set<string>>(new Set())
  const [saving, setSaving]         = useState<string | null>(null)
  const [viewMode, setViewMode]     = useState<'chips' | 'weekly'>('weekly')
  const [pendingSave, setPendingSave] = useState<'all' | null>(null)
  const [saveAllError, setSaveAllError] = useState<string | null>(null)
  
  // ★ 確定シフトメール送信
  type ConfirmTarget = {
    assistant_id: string; name: string; email: string; days_count: number
    changed: boolean; is_test_email: boolean
  }
  type ConfirmPreview = {
    total_confirmed: number; can_send: number; no_email: number
    targets: ConfirmTarget[]
    no_email_names: string[]; last_sent_at: string | null
  }
  type SendResult = { assistant_id: string; name: string; success: boolean; reason?: string }

  const [confirmPreview,  setConfirmPreview]  = useState<ConfirmPreview | null>(null)
  const [showConfirmSend, setShowConfirmSend] = useState(false)
  const [confirmStep,     setConfirmStep]     = useState<'select' | 'confirm' | 'result'>('select')
  const [selectedIds,     setSelectedIds]     = useState<Set<string>>(new Set())
  const [confirmSending,  setConfirmSending]  = useState(false)
  const [sendResults,     setSendResults]     = useState<SendResult[]>([])

  // PC設備数チェックの上限（制作進行設定 > 制作環境設定 で変更可能。取得失敗時は旧ハードコード値にフォールバック）
  const DEFAULT_PC_LIMITS = { windows: 2, mac: 3 }
  const [pcLimits, setPcLimits] = useState(DEFAULT_PC_LIMITS)
  const [pcLimitsEnabled, setPcLimitsEnabled] = useState(true)
  // notes/is_enabled を保存時に失わないよう、取得した設定値をまるごと保持しておく
  // （制作環境設定ページと同じ production_resource_settings を共有。二重管理はしない）
  const [resourceSettingsFull, setResourceSettingsFull] = useState<ResourceSettings | null>(null)
  const [showPcSettingsEdit, setShowPcSettingsEdit] = useState(false)
  const applyResourceSettings = (s: ResourceSettings) => {
    setResourceSettingsFull(s)
    setPcLimitsEnabled(s.is_enabled === 1)
    setPcLimits({ windows: s.windows_capacity, mac: s.mac_capacity })
  }
  useEffect(() => {
    api.productionPlanning.getResourceSettings()
      .then(applyResourceSettings)
      .catch(() => { /* 取得失敗時はデフォルト値のまま */ })
  }, [])

  // ダッシュボード「カレンダー・TODO」と同じ予定（読み取り専用の補助表示。シフト側DBにはコピーしない）
  const [scheduleByDate, setScheduleByDate] = useState<Map<string, ScheduleEvent[]>>(new Map())
  const [openScheduleDate, setOpenScheduleDate] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    setOpenScheduleDate(null)
    fetchMonthScheduleEvents(month)
      .then(({ events, autoEvs }) => { if (!cancelled) setScheduleByDate(groupEventsByDate([...events, ...autoEvs])) })
      .catch(() => { if (!cancelled) setScheduleByDate(new Map()) })
    return () => { cancelled = true }
  }, [month])

  const load = useCallback(async () => {
    setLoading(true)
    const raw: any = await api.confirmedShifts.list(month)
    const [y, m] = month.split('-').map(Number)
    const daysInMonth = raw.days_in_month ?? new Date(y, m, 0).getDate()
    const assistants: ConfirmedShiftAsst[] = (raw.assistants ?? []).map((a: any) => ({
      assistant_id:    a.assistant_id ?? a.id,
      assistant_name:  a.assistant_name ?? a.name,
      role_type:       a.role_type ?? 'general',
      pc_type:         a.pc_type ?? 'unknown',
      daily_rate:      Number(a.daily_rate ?? 0),
      request_id:      a.request_id ?? null,
      available_dates: a.available_dates ?? a.request_dates ?? '[]',
      shift_id:        a.shift_id ?? null,
      confirmed_dates: a.confirmed_dates ?? '[]',
      confirmed_note:  a.confirmed_note ?? a.note ?? null,
      shift_status:    a.shift_status ?? null,
    }))
    const json: ShiftMonthData = { month: raw.month ?? month, days_in_month: daysInMonth, assistants }
    setData(json)
    const init: Record<string, string[]> = {}
    for (const a of assistants) {
      try { init[a.assistant_id] = JSON.parse(a.confirmed_dates ?? '[]') }
      catch { init[a.assistant_id] = [] }
    }
    setLocalDates(init)
    setDirty(new Set())
    setLoading(false)
  }, [month])

  useEffect(() => { load() }, [load])
  // ★ 確定通知プレビューを取得（変更があった人を初期選択）
  useEffect(() => {
    api.shiftConfirmationNotifications.preview(month)
      .then((preview) => {
        setConfirmPreview(preview)
        setSelectedIds(new Set(preview.targets.filter((t) => t.changed).map((t) => t.assistant_id)))
      })
      .catch(() => setConfirmPreview(null))
  }, [month, data])   // data が変わる（保存後）にも更新

  type PcWarning = { date: string; windows: number; mac: number }

  // 日付ごとの Windows/Mac 使用台数・上限状態。選択状態（localDates）が変わるたびに再計算される。
  const pcCapacityByDate = useMemo<Record<string, PcCapacityStatus>>(() => {
    const map: Record<string, PcCapacityStatus> = {}
    if (!data) return map
    const countByDate: Record<string, { windows: number; mac: number; unset: number }> = {}
    for (const asst of data.assistants) {
      const pc = asst.pc_type ?? 'unknown'
      for (const date of localDates[asst.assistant_id] ?? []) {
        if (!countByDate[date]) countByDate[date] = { windows: 0, mac: 0, unset: 0 }
        if (pc === 'windows') countByDate[date].windows++
        else if (pc === 'mac') countByDate[date].mac++
        else countByDate[date].unset++
      }
    }
    for (const [date, c] of Object.entries(countByDate)) {
      map[date] = {
        date,
        macUsed: c.mac, macLimit: pcLimits.mac,
        windowsUsed: c.windows, windowsLimit: pcLimits.windows,
        macStatus: pcLimitsEnabled ? pcStatusOf(c.mac, pcLimits.mac) : 'ok',
        windowsStatus: pcLimitsEnabled ? pcStatusOf(c.windows, pcLimits.windows) : 'ok',
        hasUnset: c.unset > 0,
      }
    }
    return map
  }, [data, localDates, pcLimits, pcLimitsEnabled])

  // 上限超過（over）のみを警告バナー・保存前確認の対象にする（上限ぴったり＝fullは表・セル表示のみで案内）
  const pcWarnings = useMemo<PcWarning[]>(() => {
    return Object.values(pcCapacityByDate)
      .filter((c) => c.macStatus === 'over' || c.windowsStatus === 'over')
      .map((c) => ({ date: c.date, windows: c.windowsUsed, mac: c.macUsed }))
      .sort((a, b) => a.date.localeCompare(b.date))
  }, [pcCapacityByDate])

  const overLimitDates = useMemo(() => new Set(pcWarnings.map((w) => w.date)), [pcWarnings])
  const fullOnlyDates = useMemo(() => new Set(
    Object.values(pcCapacityByDate)
      .filter((c) => (c.macStatus === 'full' || c.windowsStatus === 'full') && c.macStatus !== 'over' && c.windowsStatus !== 'over')
      .map((c) => c.date)
  ), [pcCapacityByDate])

  const formatWarningDate = (date: string) => {
    const [, m, d] = date.split('-')
    return `${Number(m)}/${Number(d)}（${DOW_LABEL[getDow(date)]}）`
  }

  const renderPcWarningLine = (w: PcWarning) => {
    const parts: string[] = []
    if (w.windows > pcLimits.windows) parts.push(`Windows ${w.windows}名（上限${pcLimits.windows}名）`)
    if (w.mac > pcLimits.mac)         parts.push(`Mac ${w.mac}名（上限${pcLimits.mac}名）`)
    return `${formatWarningDate(w.date)} — ${parts.join(' / ')}`
  }

  if (loading || !data) return <div className="loading">LOADING...</div>

  const allDays = Array.from({ length: data.days_in_month }, (_, i) => {
    const d = i + 1
    return `${month}-${String(d).padStart(2, '0')}`
  })


  const toggleDate = (assistantId: string, date: string) => {
    setLocalDates((prev) => {
      const cur = prev[assistantId] ?? []
      const next = cur.includes(date) ? cur.filter((d) => d !== date) : [...cur, date]
      return { ...prev, [assistantId]: next }
    })
    setDirty((prev) => new Set(prev).add(assistantId))
  }

  const save = async (assistantId: string) => {
    setSaving(assistantId)
    try {
      await api.confirmedShifts.save(assistantId, month, localDates[assistantId] ?? [])
      setDirty((prev) => { const s = new Set(prev); s.delete(assistantId); return s })
      const sorted = [...(localDates[assistantId] ?? [])].sort()
      setData((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          assistants: prev.assistants.map((a) =>
            a.assistant_id === assistantId
              ? { ...a, confirmed_dates: JSON.stringify(sorted), shift_id: a.shift_id ?? '__saved__' }
              : a
          ),
        }
      })
    } finally { setSaving(null) }
  }

  const saveAll = async () => {
    setSaving('__all__')
    setSaveAllError(null)
    try {
      // Worker 側に /confirmed-shifts/bulk がないため、既存の個別保存APIを順番に呼ぶ
      const targets = data.assistants.filter((a) => dirty.has(a.assistant_id))
      for (const a of targets) {
        await api.confirmedShifts.save(a.assistant_id, month, localDates[a.assistant_id] ?? [])
      }
      setDirty(new Set())
      await load()
    } catch (err: any) {
      setSaveAllError(err?.message ?? '保存に失敗しました')
    } finally { setSaving(null) }
  }

  const copyAllRequests = () => {
    // シフト希望を提出したアシスタントの希望日を localDates に反映する。
    // 希望未提出者（request_id が null）の確定日は変更しない。
    setLocalDates((prev) => {
      const next = { ...prev }
      for (const a of data.assistants) {
        if (!a.request_id) continue   // 希望未提出者はスキップ
        try {
          const raw = a.available_dates
          next[a.assistant_id] = raw && raw.trim() !== '' ? JSON.parse(raw) : []
        } catch {
          next[a.assistant_id] = []
        }
      }
      return next
    })
    // 希望提出者だけ dirty にする
    setDirty((prev) => {
      const s = new Set(prev)
      for (const a of data.assistants) {
        if (a.request_id) s.add(a.assistant_id)
      }
      return s
    })
  }

  const confirmedTotal  = data.assistants.filter((a) => a.shift_id).length
  const requestedTotal  = data.assistants.filter((a) => a.request_id).length
  const projectedManDays = data.assistants.reduce((sum, a) => sum + (localDates[a.assistant_id] ?? []).length, 0)
  const projectedCost    = data.assistants.reduce((sum, a) => sum + (localDates[a.assistant_id] ?? []).length * (a.daily_rate ?? 0), 0)
  const sendConfirmedShifts = async () => {
    setConfirmSending(true)
    try {
      const result = await api.shiftConfirmationNotifications.send(month, [...selectedIds])
      setSendResults(result.results)
      setConfirmStep('result')
      // プレビューを更新（last_sent_at / changed フラグを反映）
      api.shiftConfirmationNotifications.preview(month)
        .then((preview) => {
          setConfirmPreview(preview)
          setSelectedIds(new Set(preview.targets.filter((t) => t.changed).map((t) => t.assistant_id)))
        })
        .catch(() => {})
    } catch (ex: any) {
      alert(`送信失敗: ${ex.message}`)
      setConfirmStep('select')
    } finally {
      setConfirmSending(false)
    }
  }

  const closeConfirmModal = () => {
    setShowConfirmSend(false)
    setConfirmStep('select')
    setSendResults([])
  }

  return (
    <div>
      {showPcSettingsEdit && resourceSettingsFull && (
        <PcCapacityEditModal
          current={resourceSettingsFull}
          onClose={() => setShowPcSettingsEdit(false)}
          onSaved={applyResourceSettings}
        />
      )}
      {pendingSave !== null && (
        <div className="overlay" onClick={(e) => e.target === e.currentTarget && setPendingSave(null)}>
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <div className="modal-title">⚠ PC設備上限超過の確認</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setPendingSave(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 13, marginBottom: 12 }}>
                PC設備数の上限を超えている日があります。このまま保存しますか？
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {pcWarnings.map((w) => (
                  <div key={w.date} style={{ fontSize: 12, color: 'var(--amber)', fontWeight: 500 }}>
                    {renderPcWarningLine(w)}
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setPendingSave(null)}>キャンセル</button>
              <button className="btn btn-primary" style={{ background: 'var(--amber)', borderColor: 'var(--amber)' }}
                onClick={() => { setPendingSave(null); saveAll() }}>
                このまま保存する
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ★ 確定シフトメール送信モーダル */}
      {showConfirmSend && confirmPreview && (() => {
        const selectedTargets = confirmPreview.targets.filter((t) => selectedIds.has(t.assistant_id))
        const changedCount    = confirmPreview.targets.filter((t) => t.changed).length

        /* ── ステップ1: 送信対象選択 ── */
        if (confirmStep === 'select') return (
          <div className="overlay" onClick={(e) => e.target === e.currentTarget && closeConfirmModal()}>
            <div className="modal" style={{ maxWidth: 520 }}>
              <div className="modal-header">
                <div className="modal-title">確定シフトをメール送信</div>
                <button className="btn btn-ghost btn-sm" onClick={closeConfirmModal}>✕</button>
              </div>
              <div className="modal-body">

                {/* 一括選択ボタン */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => setSelectedIds(new Set(confirmPreview.targets.filter((t) => t.changed).map((t) => t.assistant_id)))}
                    disabled={changedCount === 0}
                  >
                    変更があった人だけ選択
                    {changedCount > 0 && <span style={{ marginLeft: 4, fontSize: 10, background: 'var(--blue)', color: '#fff', borderRadius: 10, padding: '1px 5px' }}>{changedCount}</span>}
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => setSelectedIds(new Set(confirmPreview.targets.map((t) => t.assistant_id)))}
                  >全員選択</button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setSelectedIds(new Set())}
                    disabled={selectedIds.size === 0}
                  >選択解除</button>
                </div>

                {/* 対象者チェックリスト */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 14, maxHeight: 320, overflowY: 'auto' }}>
                  {confirmPreview.targets.map((t) => {
                    const checked = selectedIds.has(t.assistant_id)
                    return (
                      <label
                        key={t.assistant_id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px',
                          borderRadius: 6, cursor: 'pointer',
                          background: checked ? 'var(--blue-light, rgba(41,128,185,0.08))' : 'transparent',
                          border: checked ? '1px solid rgba(41,128,185,0.3)' : '1px solid transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setSelectedIds((prev) => {
                              const next = new Set(prev)
                              checked ? next.delete(t.assistant_id) : next.add(t.assistant_id)
                              return next
                            })
                          }}
                          style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--blue)' }}
                        />
                        <span style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>{t.name}</span>
                        <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{t.days_count}日分</span>
                        {t.changed
                          ? <span style={{ fontSize: 10, background: 'var(--blue)', color: '#fff', borderRadius: 10, padding: '1px 6px' }}>変更あり</span>
                          : <span style={{ fontSize: 10, background: 'var(--paper-3)', color: 'var(--ink-3)', borderRadius: 10, padding: '1px 6px' }}>変更なし</span>
                        }
                        {t.is_test_email && (
                          <span style={{ fontSize: 10, background: 'var(--amber)', color: '#fff', borderRadius: 10, padding: '1px 6px' }}>仮メール</span>
                        )}
                      </label>
                    )
                  })}
                </div>

                {/* メール未登録の警告 */}
                {confirmPreview.no_email > 0 && (
                  <div style={{
                    background: 'var(--amber-light)', border: '1px solid rgba(176,122,18,0.25)',
                    borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 12,
                  }}>
                    <span style={{ fontWeight: 600, color: 'var(--amber)' }}>⚠ メール未登録（送信不可）: </span>
                    {confirmPreview.no_email_names.join('、')}
                  </div>
                )}

                {/* 最終送信日時 */}
                {confirmPreview.last_sent_at && (
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', textAlign: 'right' }}>
                    最終送信: {confirmPreview.last_sent_at.slice(0, 16).replace('T', ' ')}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline" onClick={closeConfirmModal}>キャンセル</button>
                <button
                  className="btn btn-primary"
                  onClick={() => setConfirmStep('confirm')}
                  disabled={selectedIds.size === 0}
                >
                  {selectedIds.size === 0
                    ? 'メール送信（0名選択中）'
                    : `選択した${selectedIds.size}名にメール送信`}
                </button>
              </div>
            </div>
          </div>
        )

        /* ── ステップ2: 送信確認 ── */
        if (confirmStep === 'confirm') return (
          <div className="overlay" onClick={(e) => e.target === e.currentTarget && setConfirmStep('select')}>
            <div className="modal" style={{ maxWidth: 460 }}>
              <div className="modal-header">
                <div className="modal-title">送信確認</div>
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirmStep('select')}>✕</button>
              </div>
              <div className="modal-body">
                <div style={{ fontSize: 14, marginBottom: 14 }}>
                  以下の <strong>{selectedTargets.length}名</strong> に確定シフトをメール送信します。よろしいですか？
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
                  {selectedTargets.map((t) => (
                    <div key={t.assistant_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, padding: '5px 8px', borderRadius: 5, background: 'var(--paper-2)' }}>
                      <span style={{ fontWeight: 600 }}>{t.name}</span>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{t.days_count}日分</span>
                        {t.is_test_email && (
                          <span style={{ fontSize: 10, background: 'var(--amber)', color: '#fff', borderRadius: 10, padding: '1px 6px' }}>仮メール</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {selectedTargets.some((t) => t.is_test_email) && (
                  <div style={{ fontSize: 12, color: 'var(--amber)', background: 'var(--amber-light)', padding: '8px 10px', borderRadius: 5 }}>
                    ⚠ 仮メールアドレス（@mangaos.test）が含まれています。実際にはメールが届きません。
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline" onClick={() => setConfirmStep('select')}>← 戻る</button>
                <button
                  className="btn btn-primary"
                  onClick={sendConfirmedShifts}
                  disabled={confirmSending}
                >
                  {confirmSending ? '送信中...' : `${selectedTargets.length}名に送信する`}
                </button>
              </div>
            </div>
          </div>
        )

        /* ── ステップ3: 送信結果 ── */
        const successCount = sendResults.filter((r) => r.success).length
        const failCount    = sendResults.filter((r) => !r.success).length
        return (
          <div className="overlay">
            <div className="modal" style={{ maxWidth: 460 }}>
              <div className="modal-header">
                <div className="modal-title">
                  {failCount === 0 ? '✓ 送信完了' : '送信結果'}
                </div>
                <button className="btn btn-ghost btn-sm" onClick={closeConfirmModal}>✕</button>
              </div>
              <div className="modal-body">
                {/* サマリー */}
                <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
                  <div style={{ background: 'var(--green-light)', border: '1px solid var(--green-border)', borderRadius: 6, padding: '8px 14px', flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>送信成功</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--green)' }}>{successCount}名</div>
                  </div>
                  {failCount > 0 && (
                    <div style={{ background: 'var(--amber-light)', border: '1px solid rgba(176,122,18,0.3)', borderRadius: 6, padding: '8px 14px', flex: 1, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>送信失敗</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--amber)' }}>{failCount}名</div>
                    </div>
                  )}
                </div>
                {/* 1人ずつ結果 */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {sendResults.map((r) => (
                    <div key={r.assistant_id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      fontSize: 13, padding: '5px 8px', borderRadius: 5,
                      background: r.success ? 'var(--green-light)' : 'var(--amber-light)',
                    }}>
                      <span style={{ fontWeight: 600 }}>{r.name}</span>
                      <span style={{ fontSize: 12, color: r.success ? 'var(--green)' : 'var(--amber)' }}>
                        {r.success ? '✓ 送信済' : `✗ ${r.reason ?? '失敗'}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={closeConfirmModal}>閉じる</button>
              </div>
            </div>
          </div>
        )
      })()}
 
      {pcWarnings.length > 0 && (
        <div style={{
          background: 'var(--amber-light)', border: '1px solid rgba(176,122,18,0.3)',
          borderRadius: 8, padding: '12px 16px', marginBottom: 14,
        }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--amber)', marginBottom: 6 }}>
            ⚠ PC設備数の上限を超えている日があります
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {pcWarnings.map((w) => (
              <div key={w.date} style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                {renderPcWarningLine(w)}
              </div>
            ))}
          </div>
        </div>
      )}

      {saveAllError && (
        <div style={{
          background: 'var(--red-light)', border: '1px solid rgba(192,57,43,0.25)',
          borderRadius: 8, padding: '10px 16px', marginBottom: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <span style={{ fontSize: 13, color: 'var(--red)' }}>⚠ 保存失敗: {saveAllError}</span>
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', fontSize: 16 }}
            onClick={() => setSaveAllError(null)}>✕</button>
        </div>
      )}

      {/* ヘッダー行（既存の <div style={{ display: 'flex', ...}}> の中のボタン群に追記） */}
      {/* ヘッダー行 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div className="metric" style={{ minWidth: 100, padding: '10px 14px' }}>
            <div className="metric-label">希望提出</div>
            <div className="metric-value">{requestedTotal}/{data.assistants.length}名</div>
          </div>
          <div className="metric" style={{ minWidth: 100, padding: '10px 14px' }}>
            <div className="metric-label">確定済み</div>
            <div className={`metric-value ${confirmedTotal === data.assistants.length ? 'green' : 'amber'}`}>
              {confirmedTotal}/{data.assistants.length}名
            </div>
          </div>
          <div className="metric" style={{ minWidth: 110, padding: '10px 14px' }}>
            <div className="metric-label">予定人足数</div>
            <div className="metric-value">{projectedManDays}<span style={{ fontSize: 12, fontWeight: 400 }}> 人足</span></div>
            <div className="metric-sub">確定シフト合計</div>
          </div>
          <div className="metric" style={{ minWidth: 130, padding: '10px 14px' }}>
            <div className="metric-label">予定人件費</div>
            <div className={`metric-value ${projectedCost > 450000 ? 'red' : projectedCost > 400000 ? 'amber' : ''}`}>
              ¥{projectedCost.toLocaleString('ja-JP')}
            </div>
            <div className="metric-sub">日給×確定日数（交通費除く）</div>
          </div>
          {dirty.size > 0 && (
            <div className="metric" style={{ minWidth: 100, padding: '10px 14px', background: 'var(--amber-light)' }}>
              <div className="metric-label">未保存</div>
              <div className="metric-value amber">{dirty.size}名</div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* 表示切り替えボタン */}
          <div style={{ display: 'flex', border: '1px solid var(--paper-3)', borderRadius: 6, overflow: 'hidden' }}>
            <button
              className={viewMode === 'chips' ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
              style={{ borderRadius: 0, fontSize: 11 }}
              onClick={() => setViewMode('chips')}
            >
              日付チップ
            </button>
            <button
              className={viewMode === 'weekly' ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
              style={{ borderRadius: 0, borderLeft: '1px solid var(--paper-3)', fontSize: 11 }}
              onClick={() => setViewMode('weekly')}
            >
              週表示
            </button>
          </div>
                    {/* ★ 追加: 確定シフトメール送信ボタン */}
          {confirmPreview && (
            <button
              className="btn btn-outline btn-sm"
              style={{
                color:       confirmPreview.can_send > 0 ? 'var(--blue)' : 'var(--ink-3)',
                borderColor: confirmPreview.can_send > 0 ? 'var(--blue)' : undefined,
              }}
              onClick={() => setShowConfirmSend(true)}
              disabled={confirmPreview.total_confirmed === 0}
              title={
                confirmPreview.total_confirmed === 0
                  ? '確定シフトがありません'
                  : `${confirmPreview.can_send}名にメール送信（未登録${confirmPreview.no_email}名）`
              }
            >
              ✉ 確定シフトをメール送信
              {confirmPreview.no_email > 0 && (
                <span style={{
                  marginLeft: 4, fontSize: 10,
                  background: 'var(--amber)', color: '#fff',
                  borderRadius: 10, padding: '1px 5px',
                }}>
                  {confirmPreview.no_email}名未登録
                </span>
              )}
            </button>
          )}
          <button className="btn btn-outline btn-sm" onClick={copyAllRequests}>希望日を全員にコピー</button>
          {dirty.size > 0 && (
            <button className="btn btn-ghost btn-sm"
              onClick={() => {
                const init: Record<string, string[]> = {}
                for (const a of data.assistants) init[a.assistant_id] = JSON.parse(a.confirmed_dates ?? '[]')
                setLocalDates(init); setDirty(new Set())
              }}
              style={{ color: 'var(--ink-3)', fontSize: 12 }}
            >
              ↩ 変更を取り消す
            </button>
          )}
          <button className="btn btn-primary btn-sm" onClick={() => pcWarnings.length > 0 ? setPendingSave('all') : saveAll()} disabled={saving === '__all__' || dirty.size === 0}>
            {saving === '__all__' ? '保存中...' : `全員を保存 (${dirty.size}名)`}
          </button>
        </div>
      </div>

      {/* 凡例 */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 14, fontSize: 11, color: 'var(--ink-3)', flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 14, height: 14, borderRadius: 3, background: 'var(--green)', display: 'inline-block' }} />確定
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 14, height: 14, borderRadius: 3, background: 'var(--amber-light)', border: '1px solid #b07a12', display: 'inline-block' }} />希望のみ
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 14, height: 14, borderRadius: 3, background: 'var(--paper-3)', display: 'inline-block' }} />なし
        </span>
        <span style={{ color: '#c0392b' }}>日=日曜</span>
        <span style={{ color: '#2980b9' }}>土=土曜</span>
        {pcLimitsEnabled && (
          <>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, border: '2px solid var(--amber)', display: 'inline-block' }} />PC台数上限ちょうど
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, border: '2px solid var(--red)', display: 'inline-block' }} />PC台数上限超過
            </span>
          </>
        )}
        {viewMode === 'weekly' && <span style={{ color: 'var(--ink-3)' }}>● クリックで確定/解除できます</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
          PC上限：Win {pcLimits.windows} / Mac {pcLimits.mac}
          {resourceSettingsFull && (
            <button
              onClick={() => setShowPcSettingsEdit(true)}
              title="Windows/Macの同時作業可能人数を変更します（制作環境設定と共通）"
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
                color: 'var(--ink-3)', fontSize: 11, textDecoration: 'underline', textUnderlineOffset: 2,
              }}
            >
              ⚙ 編集
            </button>
          )}
        </span>
      </div>

      {/* ──────── 予定（ダッシュボードの予定を読み取り専用で補助表示） ──────── */}
      {scheduleByDate.size > 0 && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--paper-2)', borderRadius: 6, fontSize: 12 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>制作予定（クリックで詳細）:</span>
            {[...scheduleByDate.entries()]
              .filter(([d, list]) => d.startsWith(month) && list.length > 0)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([d, list]) => (
                <button key={d}
                  onClick={() => setOpenScheduleDate(openScheduleDate === d ? null : d)}
                  title={list.map((e) => e.title).join('\n')}
                  style={{
                    border: '1px solid var(--paper-3)', borderRadius: 10, padding: '1px 8px', cursor: 'pointer',
                    fontSize: 11, background: openScheduleDate === d ? 'var(--blue-light)' : 'var(--paper)', color: 'var(--ink-2)',
                  }}>
                  {Number(d.slice(5, 7))}/{Number(d.slice(8))} <span style={{ color: 'var(--blue)' }}>●</span> {list.length === 1 ? '予定あり' : `予定 ${list.length}件`}
                </button>
              ))}
          </div>
          {openScheduleDate && (scheduleByDate.get(openScheduleDate) ?? []).length > 0 && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--paper-3)' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{openScheduleDate} の予定</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {(scheduleByDate.get(openScheduleDate) ?? []).map((e, i) => (
                  <li key={e.id ?? i} style={{ textDecoration: e.is_done ? 'line-through' : undefined }}>
                    {e.title}{e.memo ? <span style={{ color: 'var(--ink-3)' }}>（{e.memo}）</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ──────── 日付チップ表示 ──────── */}
      {viewMode === 'chips' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.assistants.map((a) => {
            const requested = new Set<string>(JSON.parse(a.available_dates ?? '[]'))
            const confirmed = new Set<string>(localDates[a.assistant_id] ?? [])
            const isDirty   = dirty.has(a.assistant_id)
            return (
              <div key={a.assistant_id} className="card" style={{ borderLeft: isDirty ? '3px solid var(--amber)' : undefined }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--paper-2)' }}>
                  <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{a.assistant_name}</span>
                  <RoleBadge role={a.role_type} />
                  <PcBadge pcType={a.pc_type} />
                  {!a.request_id && <span className="badge badge-gray" style={{ fontSize: 10 }}>希望未提出</span>}
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-3)' }}>
                    希望{requested.size}日 / 確定{confirmed.size}日
                  </span>
                  {isDirty && (
                    <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }}
                      onClick={() => save(a.assistant_id)} disabled={saving === a.assistant_id}>
                      {saving === a.assistant_id ? '…' : '保存'}
                    </button>
                  )}
                </div>
                <div style={{ padding: '10px 16px', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {allDays.map((date) => {
                    const isConf   = confirmed.has(date)
                    const isReq    = requested.has(date)
                    const dow      = getDow(date)
                    const isSun    = dow === 0
                    const isSat    = dow === 6
                    const isWeekend = isSun || isSat

                    let bg     = isWeekend ? 'var(--paper-2)' : 'var(--paper-3)'
                    let color  = isSun ? '#c0392b' : isSat ? '#2980b9' : 'var(--ink-3)'
                    let border = isWeekend ? `1px solid ${isSun ? '#f5c6c2' : 'var(--blue-light)'}` : '1px solid transparent'

                    if (isConf && isReq)  { bg = 'var(--green)';  color = '#fff';         border = '1px solid transparent' }
                    else if (isConf)      { bg = 'var(--green-light)';        color = 'var(--green)'; border = '1px solid var(--green)' }
                    else if (isReq)       { bg = 'var(--amber-light)';        color = isSun ? '#c0392b' : isSat ? '#2980b9' : 'var(--amber)'; border = '1px solid var(--amber)' }

                    // 上限状態: 確定出勤しているチップだけ、本人のPC種別が上限に達している場合に枠を強調する。
                    const cap = pcCapacityByDate[date]
                    const asstPc = a.pc_type === 'windows' || a.pc_type === 'mac' ? a.pc_type : null
                    const myStatus: PcStatus | undefined = asstPc === 'mac' ? cap?.macStatus : asstPc === 'windows' ? cap?.windowsStatus : undefined
                    const cellOver = isConf && myStatus === 'over'
                    const cellFull = isConf && myStatus === 'full'
                    if (cellOver) border = '2px solid var(--red)'
                    else if (cellFull) border = '2px solid var(--amber)'

                    const pcLabel = asstPc === 'mac' ? 'Mac' : asstPc === 'windows' ? 'Windows' : null
                    const capTitle = pcLabel && cap && (cellOver || cellFull)
                      ? `／${pcLabel}${cellOver ? '上限超過' : '上限ちょうど'}：${asstPc === 'mac' ? cap.macUsed : cap.windowsUsed}/${asstPc === 'mac' ? cap.macLimit : cap.windowsLimit}台`
                      : ''

                    return (
                      <button key={date} onClick={() => toggleDate(a.assistant_id, date)}
                        title={`${date} (${DOW_LABEL[dow]})${isReq ? ' 希望' : ''}${isConf ? ' 確定' : ''}${capTitle}`}
                        style={{
                          width: 34, height: 44, borderRadius: 6, position: 'relative',
                          background: bg, color, border,
                          fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center', gap: 1, padding: 0,
                        }}>
                        <span style={{ fontSize: 12, lineHeight: 1 }}>{Number(date.slice(8))}</span>
                        <span style={{ fontSize: 9, lineHeight: 1, opacity: isConf ? 0.9 : 0.75 }}>{DOW_LABEL[dow]}</span>
                        {(cellOver || cellFull) && (
                          <span style={{
                            position: 'absolute', top: 1, right: 2, fontSize: 8, lineHeight: 1,
                            color: cellOver ? 'var(--red)' : 'var(--amber)',
                          }}>⚠</span>
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ──────── 週テーブル表示 ──────── */}
      {viewMode === 'weekly' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%', tableLayout: 'auto' }}>
            <thead>
              <tr>
                {/* 名前列 */}
                <th style={{
                  padding: '6px 12px', textAlign: 'left', position: 'sticky', left: 0,
                  background: 'var(--paper-2)', zIndex: 2,
                  borderBottom: '2px solid var(--paper-3)', whiteSpace: 'nowrap',
                }}>
                  名前
                </th>
                {/* 日付列（週区切り線付き） */}
                {allDays.map((date) => {
                  const dow    = getDow(date)
                  const isSun  = dow === 0
                  const isSat  = dow === 6
                  const isFirstDayOfWeek = dow === 0  // 週の先頭に太めのボーダー
                  const overLimit = overLimitDates.has(date)
                  const fullOnly  = fullOnlyDates.has(date)
                  const cap = pcCapacityByDate[date]
                  const capTitle = cap
                    ? `Mac ${cap.macUsed}/${cap.macLimit}台${cap.macStatus !== 'ok' ? (cap.macStatus === 'over' ? '（超過）' : '（上限）') : ''}／Windows ${cap.windowsUsed}/${cap.windowsLimit}台${cap.windowsStatus !== 'ok' ? (cap.windowsStatus === 'over' ? '（超過）' : '（上限）') : ''}`
                    : ''
                  const schedList = scheduleByDate.get(date) ?? []
                  const schedTitle = schedList.length > 0 ? `予定 ${schedList.length}件:\n${schedList.map((e) => e.title).join('\n')}` : ''
                  return (
                    <th key={date} title={[capTitle, schedTitle].filter(Boolean).join('\n')} style={{
                      padding: '4px 2px', minWidth: 34, textAlign: 'center',
                      color: isSun ? '#c0392b' : isSat ? '#2980b9' : 'var(--ink)',
                      background: overLimit ? 'var(--red-light)' : fullOnly ? 'var(--amber-light)' : isSun ? 'var(--red-light)' : isSat ? 'var(--blue-light)' : 'var(--paper-2)',
                      borderLeft: isFirstDayOfWeek ? '2px solid var(--paper-3)' : '1px solid var(--paper-2)',
                      borderBottom: overLimit ? '3px solid var(--red)' : fullOnly ? '2px solid var(--amber)' : '2px solid var(--paper-3)',
                    }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 12 }}>
                        {Number(date.slice(8))}
                      </div>
                      <div style={{ fontSize: 9, opacity: 0.75 }}>
                        {DOW_LABEL[dow]}{overLimit ? ' ⚠' : fullOnly ? ' •' : ''}
                      </div>
                      {schedList.length > 0 && (
                        <div style={{ fontSize: 8, lineHeight: 1, color: 'var(--blue)' }}>
                          ●{schedList.length > 1 ? schedList.length : ''}
                        </div>
                      )}
                      {cap && (cap.macUsed > 0 || cap.windowsUsed > 0) && (
                        <div style={{ fontSize: 7, lineHeight: 1.2, marginTop: 1, fontFamily: 'var(--font-mono)' }}>
                          {cap.macUsed > 0 && (
                            <div style={{ color: pcStatusColor(cap.macStatus), fontWeight: cap.macStatus !== 'ok' ? 700 : 400 }}>
                              M{cap.macUsed}/{cap.macLimit}
                            </div>
                          )}
                          {cap.windowsUsed > 0 && (
                            <div style={{ color: pcStatusColor(cap.windowsStatus), fontWeight: cap.windowsStatus !== 'ok' ? 700 : 400 }}>
                              W{cap.windowsUsed}/{cap.windowsLimit}
                            </div>
                          )}
                        </div>
                      )}
                    </th>
                  )
                })}
                {/* 集計列 */}
                <th style={{
                  padding: '6px 10px', textAlign: 'right',
                  background: 'var(--paper-2)', borderBottom: '2px solid var(--paper-3)',
                  borderLeft: '2px solid var(--paper-3)', whiteSpace: 'nowrap',
                }}>
                  確定/希望
                </th>
              </tr>
            </thead>
            <tbody>
              {data.assistants.map((a, rowIdx) => {
                const requested = new Set<string>(JSON.parse(a.available_dates ?? '[]'))
                const confirmed = new Set<string>(localDates[a.assistant_id] ?? [])
                const isDirty   = dirty.has(a.assistant_id)
                const rowBg     = rowIdx % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.018)'

                return (
                  <tr key={a.assistant_id} style={{ background: rowBg }}>
                    {/* 名前（左固定） */}
                    <td style={{
                      padding: '6px 12px', fontWeight: 600, fontSize: 13,
                      position: 'sticky', left: 0, background: isDirty ? 'var(--amber-light)' : (rowIdx % 2 === 0 ? 'var(--paper)' : 'var(--paper-2)'),
                      zIndex: 1, whiteSpace: 'nowrap',
                      borderLeft: isDirty ? '3px solid var(--amber)' : '3px solid transparent',
                      borderBottom: '1px solid var(--paper-2)',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>{a.assistant_name}</span>
                        <PcBadge pcType={a.pc_type} />
                        {!a.request_id && <span style={{ fontSize: 9, color: 'var(--ink-3)', background: 'var(--paper-3)', borderRadius: 3, padding: '1px 4px' }}>未提出</span>}
                        {isDirty && (
                          <button className="btn btn-primary" style={{ fontSize: 10, padding: '1px 6px', height: 20 }}
                            onClick={() => save(a.assistant_id)} disabled={saving === a.assistant_id}>
                            {saving === a.assistant_id ? '…' : '保存'}
                          </button>
                        )}
                      </div>
                    </td>

                    {/* 各日付セル */}
                    {allDays.map((date) => {
                      const isConf   = confirmed.has(date)
                      const isReq    = requested.has(date)
                      const dow      = getDow(date)
                      const isSun    = dow === 0
                      const isSat    = dow === 6
                      const isFirstDayOfWeek = dow === 0

                      let cellBg    = 'transparent'
                      let cellColor = 'var(--ink-3)'
                      let symbol    = ''

                      if (isConf && isReq)  { cellBg = 'var(--green)';  cellColor = '#fff';           symbol = '✓' }
                      else if (isConf)      { cellBg = 'var(--green-light)';        cellColor = 'var(--green)';   symbol = '✓' }
                      else if (isReq)       { cellBg = 'var(--amber-light)';        cellColor = isSun ? '#c0392b' : isSat ? '#2980b9' : 'var(--amber)'; symbol = '○' }
                      else if (isSun)       { cellBg = 'var(--red-light)' }
                      else if (isSat)       { cellBg = 'var(--blue-light)' }

                      // 上限状態: 空セルや希望のみセルには色をつけない。確定セルのみ、本人のPC種別が
                      // その日の上限に達しているかどうかで枠・アイコンを出す。
                      const cap = pcCapacityByDate[date]
                      const asstPc = a.pc_type === 'windows' || a.pc_type === 'mac' ? a.pc_type : null
                      const myStatus: PcStatus | undefined = asstPc === 'mac' ? cap?.macStatus : asstPc === 'windows' ? cap?.windowsStatus : undefined
                      const cellOver = isConf && myStatus === 'over'
                      const cellFull = isConf && myStatus === 'full'
                      const pcLabel = asstPc === 'mac' ? 'Mac' : asstPc === 'windows' ? 'Windows' : null
                      const capTitle = pcLabel && cap && (cellOver || cellFull)
                        ? `／${pcLabel}${cellOver ? '上限超過' : '上限ちょうど'}：${asstPc === 'mac' ? cap.macUsed : cap.windowsUsed}/${asstPc === 'mac' ? cap.macLimit : cap.windowsLimit}台`
                        : ''

                      return (
                        <td key={date}
                          onClick={() => toggleDate(a.assistant_id, date)}
                          title={`${date} (${DOW_LABEL[dow]})${isReq ? ' 希望' : ''}${isConf ? ' 確定' : ''}${capTitle}`}
                          style={{
                            width: 34, height: 32, textAlign: 'center', position: 'relative',
                            background: cellBg, color: cellColor,
                            cursor: 'pointer', fontSize: 13, fontWeight: isConf ? 700 : 400,
                            borderLeft: isFirstDayOfWeek ? '2px solid var(--paper-3)' : '1px solid var(--paper-2)',
                            borderBottom: cellOver ? '2px solid var(--red)' : cellFull ? '2px solid var(--amber)' : '1px solid var(--paper-2)',
                            userSelect: 'none',
                          }}>
                          {symbol}
                          {(cellOver || cellFull) && (
                            <span style={{
                              position: 'absolute', top: 0, right: 1, fontSize: 8, lineHeight: 1,
                              color: cellOver ? 'var(--red)' : 'var(--amber)',
                            }}>⚠</span>
                          )}
                        </td>
                      )
                    })}

                    {/* 集計セル */}
                    <td style={{
                      padding: '4px 10px', textAlign: 'right',
                      fontFamily: 'var(--font-mono)', fontSize: 12,
                      borderLeft: '2px solid var(--paper-3)',
                      borderBottom: '1px solid var(--paper-2)',
                      whiteSpace: 'nowrap',
                    }}>
                      <span style={{ fontWeight: 700 }}>{confirmed.size}</span>
                      <span style={{ color: 'var(--ink-3)' }}>/{requested.size}</span>
                    </td>
                  </tr>
                )
              })}

              {/* フッター: 確定日合計 */}
              <tr style={{ background: 'var(--paper-2)', fontWeight: 700 }}>
                <td style={{
                  padding: '6px 12px', fontSize: 12, position: 'sticky', left: 0,
                  background: 'var(--paper-2)', zIndex: 1,
                  borderTop: '2px solid var(--paper-3)',
                }}>
                  確定合計
                </td>
                {allDays.map((date) => {
                  const dow   = getDow(date)
                  const count = data.assistants.filter((a) => (localDates[a.assistant_id] ?? []).includes(date)).length
                  const isFirstDayOfWeek = dow === 0
                  const overLimit = overLimitDates.has(date)
                  const fullOnly  = fullOnlyDates.has(date)
                  return (
                    <td key={date} style={{
                      textAlign: 'center', fontSize: 11,
                      fontFamily: 'var(--font-mono)',
                      color: overLimit ? 'var(--red)' : fullOnly ? 'var(--amber)' : count > 0 ? 'var(--green)' : 'var(--ink-3)',
                      background: overLimit ? 'var(--red-light)' : fullOnly ? 'var(--amber-light)' : undefined,
                      fontWeight: count > 0 ? 700 : 400,
                      borderLeft: isFirstDayOfWeek ? '2px solid var(--paper-3)' : '1px solid var(--paper-2)',
                      borderTop: '2px solid var(--paper-3)',
                    }}>
                      {count > 0 ? count : ''}
                    </td>
                  )
                })}
                <td style={{
                  textAlign: 'right', padding: '6px 10px',
                  fontFamily: 'var(--font-mono)', fontSize: 13,
                  borderLeft: '2px solid var(--paper-3)',
                  borderTop: '2px solid var(--paper-3)',
                }}>
                  {projectedManDays}人足
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ================================================================
// メインページ
// ================================================================
export default function Personnel() {
  const today = todayJst()
  const isMobile = useIsMobile()

  const [assistants,       setAssistants]       = useState<Assistant[]>([])
  const [attendancePickerAssistants, setAttendancePickerAssistants] = useState<Assistant[]>([])
  const [overviewAssistants, setOverviewAssistants] = useState<Assistant[]>([])
  const [overviewFilter,   setOverviewFilter]   = useState<'active' | 'leaving' | 'retired' | 'all'>('active')
  const [retireModal,      setRetireModal]       = useState<{ assistant: Assistant; action: RetireAction } | null>(null)
  const [salary,        setSalary]        = useState<any>(null)
  const [shiftData,     setShiftData]     = useState<any>(null)
  const [missingData,   setMissingData]   = useState<any>(null)
  const [deadline,      setDeadline]      = useState<any>(null)
  const [paymentData,   setPaymentData]   = useState<any>(null)
  const [month,         setMonthState]    = useState<string>(getSavedMonth)
  const [loading,       setLoading]       = useState(true)
  const [showAttend,     setShowAttend]     = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const VALID_TABS = ['overview','salary','shifts','confirmed','payment','payroll'] as const
  const rawTab = searchParams.get('tab')
  const tab = (VALID_TABS as readonly string[]).includes(rawTab ?? '') ? rawTab as typeof VALID_TABS[number] : 'overview'
  const setTab = (t: typeof VALID_TABS[number]) => setSearchParams({ tab: t }, { replace: true })
  const [deleteAttendConfirm, setDeleteAttendConfirm] =
  useState<{ id: string; label: string } | null>(null)
  const [attendToast, setAttendToast] =
    useState<{ msg: string; ok: boolean } | null>(null)
  
  const showAttendToast = (msg: string, ok: boolean) => {
    setAttendToast({ msg, ok })
    setTimeout(() => setAttendToast(null), 5000)
  }
  const [undoState,     setUndoState]     = useState<{ type: 'attendance'; id: string; label: string } | null>(null)
  const [editAssistant, setEditAssistant] = useState<Assistant | null>(null)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [personnelMode,  setPersonnelMode]  = useState(false)
  const [showPersonnelMenu, setShowPersonnelMenu] = useState(false)
  const [attendanceList,setAttendanceList] = useState<any[]>([])
  const [editAttendance,setEditAttendance] = useState<any | null>(null)
  const [attendSort, setAttendSort] = useState<SortState>({ key: 'work_date', dir: 'desc' })

  const setMonth = (m: string) => {
    setMonthState(m)
    try { localStorage.setItem(MONTH_KEY, m) } catch { /* ignore */ }
  }
  const doDeleteAttendance = async (id: string) => {
    try {
      await api.attendance.delete(id)
      await load()
      showAttendToast('勤怠を削除しました', true)
    } catch (e: any) {
      const msg: string = e.message ?? '削除に失敗しました'
      if (msg.includes('MONTH_CLOSED') || msg.includes('締め済み')) {
        showAttendToast(
          'この月は締め済みのため削除できません。月次レポートから締めを解除してください。',
          false
        )
      } else {
        showAttendToast(msg || '削除に失敗しました', false)
      }
    }
  }

  const load = async () => {
    const [asts, pickerAsts, sal, shifts, missing, dl, payments, attendRecs] = await Promise.all([
      api.assistants.list(),  // 現役のみ（業務タブ・シフト未提出判定用。従来通り変更しない）
      // 勤怠入力ピッカー用: 退職予定・退職済みでも給与対象（payroll_enabled=1）なら含める
      // （最終給与のための当月勤怠入力を可能にするため。上の assistants とは別state）
      api.assistants.list({ status: 'all' }),
      api.attendance.summary(month),
      api.shiftRequests.list(month),
      api.shiftRequests.missing(month),
      api.shiftDeadlines.get(month),
      api.monthlyPayments.list(month),
      api.attendance.list(month),
    ])
    setAssistants(asts)
    setAttendancePickerAssistants(pickerAsts.filter(isPayrollEnabled))
    setSalary(sal)
    setShiftData(shifts)
    setMissingData(missing)
    setDeadline(dl)
    setPaymentData(payments)
    setAttendanceList(attendRecs)
    setLoading(false)
  }

  const loadOverview = useCallback(async () => {
    const asts = await api.assistants.list({ status: overviewFilter })
    setOverviewAssistants(asts)
  }, [overviewFilter])

  useEffect(() => { load() }, [month])
  useEffect(() => { loadOverview() }, [loadOverview])

  const toggleAttendSort = useCallback((key: string) => {
    setAttendSort((prev) => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' })
  }, [])

  const sortedAttendance = useMemo(() => {
    const list = [...(attendanceList ?? [])]
    const { key, dir } = attendSort
    list.sort((a: any, b: any) => {
      const av: any = a[key] ?? ''
      const bv: any = b[key] ?? ''
      if (typeof av === 'string' && typeof bv === 'string') {
        return dir === 'asc' ? av.localeCompare(bv, 'ja') : bv.localeCompare(av, 'ja')
      }
      return dir === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av)
    })
    return list
  }, [attendanceList, attendSort])

  if (loading) return <div className="loading">LOADING...</div>

  const totalPay = salary?.grand_total ?? 0
  const payrollAssistants = assistants.filter(isPayrollEnabled)
  const payrollAssistantIds = new Set(payrollAssistants.map((a: any) => a.id))
  const shiftMissingList = (missingData?.missing ?? []).filter((a: any) => payrollAssistantIds.has(a.id))
  const shiftEligibleTotal = payrollAssistants.length
  const shiftMissingCount = shiftMissingList.length
  const shiftSubmittedCount = Math.max(0, shiftEligibleTotal - shiftMissingCount)
  const displayMissingData = missingData
    ? { ...missingData, missing: shiftMissingList, count: shiftMissingCount }
    : null

  const TAB_LABELS: Record<string, string> = {
    overview: 'メンバー概要', salary: '給与サマリー',
    shifts: 'シフト希望', confirmed: 'シフト確定',
    payment: '支払準備', payroll: '給与申告確認',
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 'auto' }} />
        <div className="flex gap-2">
          <button className="btn btn-primary btn-sm" onClick={() => setShowAttend(true)}>+ 勤怠入力</button>
        </div>
      </div>

      <div className="grid-4" style={{ marginBottom: 20 }}>
        <div className="metric">
          <div className="metric-label">今月人件費合計</div>
          <div className={`metric-value ${totalPay > 450000 ? 'red' : totalPay > 400000 ? 'amber' : ''}`}>¥{fmt(totalPay)}</div>
          <div className="metric-sub">目標上限 ¥450,000</div>
        </div>
        <div className="metric">
          <div className="metric-label">今月の人足数</div>
          <div className="metric-value">{salary?.assistants?.reduce((s: number, a: any) => s + a.days_worked, 0) ?? 0}</div>
          <div className="metric-sub">目標 27人足</div>
        </div>
        <div className="metric">
          <div className="metric-label">アクティブ人数</div>
          <div className="metric-value">{assistants.length}</div>
        </div>
        <div className="metric">
          <div className="metric-label">残業発生者</div>
          <div className="metric-value amber">{salary?.assistants?.filter((a: any) => a.total_overtime_min > 0).length ?? 0}名</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {(['overview', 'salary', 'shifts', 'confirmed', 'payment', 'payroll'] as const).map((t) => (
          <button key={t} className={`btn btn-sm ${tab === t ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab(t)}>
            {TAB_LABELS[t]}
            {t === 'shifts' && shiftData && (
              <>
                <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.7 }}>
                  {shiftSubmittedCount}/{shiftEligibleTotal}
                </span>
                {(shiftData.needs_review_count ?? 0) > 0 && (
                  <span className="badge badge-red" style={{ marginLeft: 4, fontSize: 9, padding: '1px 5px' }}>
                    要確認{shiftData.needs_review_count}
                  </span>
                )}
              </>
            )}
          </button>
        ))}
      </div>

      {/* ── メンバー概要 ── */}
      {tab === 'overview' && (
        <div>
          {/* 人員管理モードバナー */}
          {personnelMode && (
            <div style={{
              background: 'var(--amber-light, #fffbeb)',
              border: '1px solid rgba(176,122,18,0.35)',
              borderRadius: 6,
              padding: '10px 16px',
              marginBottom: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              fontSize: 13,
            }}>
              <span style={{ flex: 1, lineHeight: 1.6 }}>
                <strong>人員管理モード中です。</strong>
                　アシスタント情報の編集・退職処理ができます。通常表示に戻す場合は終了してください。
              </span>
              <button
                className="btn btn-sm btn-outline"
                onClick={() => { setPersonnelMode(false); setOverviewFilter('active') }}
              >
                人員管理モードを終了
              </button>
            </div>
          )}

          {/* ヘッダー行 */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, gap: 8 }}>
            {/* 左側: 通常時タイトル / 管理モード時フィルター / 退職者表示バー */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {personnelMode ? (
                <>
                  <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>表示:</span>
                  {([
                    { value: 'active',  label: '現役' },
                    { value: 'leaving', label: '退職予定' },
                    { value: 'retired', label: '退職済み' },
                    { value: 'all',     label: 'すべて' },
                  ] as const).map(({ value, label }) => (
                    <button key={value}
                      className={`btn btn-sm ${overviewFilter === value ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => setOverviewFilter(value)}
                    >
                      {label}
                      {value === 'active' && overviewFilter === 'active' && (
                        <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.7 }}>{overviewAssistants.length}名</span>
                      )}
                    </button>
                  ))}
                </>
              ) : overviewFilter !== 'active' ? (
                <>
                  <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                    {overviewFilter === 'all'     ? '退職予定・退職済みを含む全員を表示中' :
                     overviewFilter === 'leaving' ? '退職予定のみ表示中' : '退職済みのみ表示中'}
                  </span>
                  <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                    onClick={() => setOverviewFilter('active')}>
                    ✕ 現役のみに戻す
                  </button>
                </>
              ) : (
                <span style={{ fontSize: 13, color: 'var(--ink-2)', fontWeight: 500 }}>
                  現役メンバー
                  <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 400, color: 'var(--ink-3)' }}>
                    {overviewAssistants.length}名
                  </span>
                </span>
              )}
            </div>

            {/* 右側: 人員管理メニュー */}
            <div style={{ position: 'relative' }}>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => setShowPersonnelMenu((v) => !v)}
                style={{ fontSize: 12 }}
              >
                人員管理 ▾
              </button>
              {showPersonnelMenu && (
                <>
                  <div
                    style={{ position: 'fixed', inset: 0, zIndex: 999 }}
                    onClick={() => setShowPersonnelMenu(false)}
                  />
                  <div className="dropdown-menu-panel" style={{
                    position: 'absolute', right: 0, top: 'calc(100% + 4px)',
                    zIndex: 1000,
                    borderRadius: 8,
                    minWidth: 220,
                    padding: '4px 0',
                  }}>
                    {!personnelMode ? (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ display: 'block', width: '100%', textAlign: 'left', borderRadius: 0, padding: '8px 16px', fontSize: 13 }}
                        onClick={() => { setPersonnelMode(true); setShowPersonnelMenu(false) }}
                      >
                        人員管理モードを開始
                      </button>
                    ) : (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ display: 'block', width: '100%', textAlign: 'left', borderRadius: 0, padding: '8px 16px', fontSize: 13 }}
                        onClick={() => { setPersonnelMode(false); setOverviewFilter('active'); setShowPersonnelMenu(false) }}
                      >
                        人員管理モードを終了
                      </button>
                    )}
                    <div style={{ borderTop: '1px solid var(--paper-2)', margin: '4px 0' }} />
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ display: 'block', width: '100%', textAlign: 'left', borderRadius: 0, padding: '8px 16px', fontSize: 13 }}
                      onClick={() => { setShowOnboarding(true); setShowPersonnelMenu(false) }}
                    >
                      ＋ 新しいアシスタントを追加
                    </button>
                    <div style={{ borderTop: '1px solid var(--paper-2)', margin: '4px 0' }} />
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ display: 'block', width: '100%', textAlign: 'left', borderRadius: 0, padding: '8px 16px', fontSize: 13 }}
                      onClick={() => { setOverviewFilter('all'); setShowPersonnelMenu(false) }}
                    >
                      退職予定・退職済みを表示
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ display: 'block', width: '100%', textAlign: 'left', borderRadius: 0, padding: '8px 16px', fontSize: 13 }}
                      onClick={() => { setOverviewFilter('retired'); setShowPersonnelMenu(false) }}
                    >
                      退職者一覧
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {overviewAssistants.length === 0 ? (
            <div className="card">
              <div className="text-muted" style={{ textAlign: 'center', padding: 24 }}>
                {overviewFilter === 'leaving' ? '退職予定のアシスタントはいません' :
                 overviewFilter === 'retired' ? '退職済みのアシスタントはいません' : 'データなし'}
              </div>
            </div>
          ) : isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {overviewAssistants.map((a) => {
                const payrollEnabled = isPayrollEnabled(a)
                const empStatus = a.employment_status ?? 'active'
                const isRetired = empStatus === 'retired'
                const isLeaving = empStatus === 'leaving'
                const isDraft = a.onboarding_status === 'draft'
                const isTraining = a.training_enabled === 1
                return (
                  <div key={a.id} className="card" style={{ padding: '14px 16px', opacity: isRetired ? 0.65 : 1 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>
                        {isRetired ? `${a.name}（退職済）` : a.name}
                      </div>
                      <RoleBadge role={a.role_type} />
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                      {isLeaving && <span className="badge badge-amber" style={{ fontSize: 10 }}>退職予定</span>}
                      {isRetired && <span className="badge badge-gray" style={{ fontSize: 10, opacity: 0.8 }}>退職済み</span>}
                      {isDraft && !isRetired && !isLeaving && <span className="badge badge-amber" style={{ fontSize: 10 }}>仮登録</span>}
                      {isTraining && !isRetired && (
                        <span className="badge badge-blue" style={{ fontSize: 10 }}>
                          研修中 {(() => {
                            const completed = Number(a.training_days_completed ?? 0)
                            const required  = Number(a.training_days_required  ?? 30)
                            const remaining = Math.max(0, required - completed)
                            return `${completed}/${required}日（あと${remaining}日）`
                          })()}
                        </span>
                      )}
                      {isRetired || isLeaving ? (
                        <span className="badge badge-gray" style={{ fontSize: 10 }}>{isLeaving ? '退職予定' : '退職済'}</span>
                      ) : payrollEnabled ? (
                        <span className="badge badge-green" style={{ fontSize: 10 }}>給与・シフト対象</span>
                      ) : (
                        <span className="badge badge-gray" style={{ fontSize: 10 }}>日報専用</span>
                      )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginTop: 10, fontSize: 12 }}>
                      <div>
                        <div style={{ color: 'var(--ink-3)', marginBottom: 2 }}>今月出勤日数</div>
                        <div className="mono" style={{ fontWeight: 600 }}>{isRetired ? '—' : `${a.total_days_this_month ?? 0}日`}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--ink-3)', marginBottom: 2 }}>来月支払額</div>
                        <div className="mono" style={{ fontWeight: 600 }}>
                          {isRetired ? '—' : payrollEnabled ? `¥${fmt(a.total_pay_this_month ?? 0)}` : '対象外'}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--ink-3)', marginBottom: 2 }}>日給</div>
                        <div className="mono" style={{ fontWeight: 600 }}>{payrollEnabled && !isRetired ? `¥${fmt(a.daily_rate)}` : '—'}</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--ink-3)', marginBottom: 2 }}>残業時給</div>
                        <div className="mono" style={{ fontWeight: 600 }}>{payrollEnabled && !isRetired ? `¥${fmt(a.overtime_hourly ?? 0)}/h` : '—'}</div>
                      </div>
                    </div>
                    {personnelMode && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--paper-2)' }}>
                        {!isRetired && (
                          <button className="btn btn-outline btn-sm" onClick={() => setEditAssistant(a)}>編集</button>
                        )}
                        {empStatus === 'active' && (
                          <button className="btn btn-outline btn-sm" style={{ color: 'var(--amber)' }}
                            onClick={() => setRetireModal({ assistant: a, action: 'leaving' })}>
                            退職予定
                          </button>
                        )}
                        {empStatus === 'leaving' && (
                          <>
                            <button className="btn btn-outline btn-sm" style={{ color: 'var(--red)' }}
                              onClick={() => setRetireModal({ assistant: a, action: 'retired' })}>
                              退職済みにする
                            </button>
                            <button className="btn btn-outline btn-sm" style={{ color: 'var(--ink-3)' }}
                              onClick={() => setRetireModal({ assistant: a, action: 'reactivate' })}>
                              現役に戻す
                            </button>
                          </>
                        )}
                        {empStatus === 'retired' && (
                          <button className="btn btn-outline btn-sm" style={{ color: 'var(--ink-3)' }}
                            onClick={() => setRetireModal({ assistant: a, action: 'reactivate' })}>
                            現役に戻す
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
          <div className="card">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>名前</th><th>役割</th><th>区分</th>
                    <th>今月出勤日数</th><th>来月支払額</th><th>日給</th><th>残業時給</th>
                    {personnelMode && <th style={{ minWidth: 200 }}>操作</th>}
                  </tr>
                </thead>
                <tbody>
                  {overviewAssistants.map((a) => {
                    const payrollEnabled = isPayrollEnabled(a)
                    const empStatus = a.employment_status ?? 'active'
                    const isRetired = empStatus === 'retired'
                    const isLeaving = empStatus === 'leaving'
                    const isDraft = a.onboarding_status === 'draft'
                    const isTraining = a.training_enabled === 1
                    return (
                      <tr key={a.id} style={{ opacity: isRetired ? 0.65 : 1 }}>
                        <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                          <span>{isRetired ? `${a.name}（退職済）` : a.name}</span>
                          {isLeaving && (
                            <span className="badge badge-amber" style={{ fontSize: 10, marginLeft: 4 }}>退職予定</span>
                          )}
                          {isRetired && (
                            <span className="badge badge-gray" style={{ fontSize: 10, marginLeft: 4, opacity: 0.8 }}>退職済み</span>
                          )}
                          {isDraft && !isRetired && !isLeaving && (
                            <span className="badge badge-amber" style={{ fontSize: 10, marginLeft: 4 }}>仮登録</span>
                          )}
                          {isTraining && !isRetired && (
                            <>
                              <span className="badge badge-blue" style={{ fontSize: 10, marginLeft: 4 }}>研修中</span>
                              <span style={{ fontSize: 10, color: 'var(--blue)', marginLeft: 4 }}>
                                {(() => {
                                  const completed = Number(a.training_days_completed ?? 0)
                                  const required  = Number(a.training_days_required  ?? 30)
                                  const remaining = Math.max(0, required - completed)
                                  return `${completed}/${required}日（あと${remaining}日）`
                                })()}
                              </span>
                            </>
                          )}
                        </td>
                        <td><RoleBadge role={a.role_type} /></td>
                        <td>
                          {isRetired || isLeaving ? (
                            <span className="badge badge-gray" style={{ fontSize: 10 }}>
                              {isLeaving ? '退職予定' : '退職済'}
                            </span>
                          ) : payrollEnabled ? (
                            <span className="badge badge-green" style={{ fontSize: 10 }}>給与・シフト対象</span>
                          ) : (
                            <span className="badge badge-gray" style={{ fontSize: 10 }}
                              title="日報提出用。給与計算・シフト希望の母数・確定シフト対象には含めません。">
                              日報専用
                            </span>
                          )}
                        </td>
                        <td className="mono">{isRetired ? '—' : `${a.total_days_this_month ?? 0}日`}</td>
                        <td className="mono">
                          {isRetired ? '—' : payrollEnabled ? `¥${fmt(a.total_pay_this_month ?? 0)}` : '対象外'}
                        </td>
                        <td className="mono">{payrollEnabled && !isRetired ? `¥${fmt(a.daily_rate)}` : '—'}</td>
                        <td className="mono" style={{ whiteSpace: 'nowrap' }}>{payrollEnabled && !isRetired ? `¥${fmt(a.overtime_hourly ?? 0)}/h` : '—'}</td>
                        {personnelMode && (
                          <td>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {!isRetired && (
                                <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                                  onClick={() => setEditAssistant(a)}>編集</button>
                              )}
                              {empStatus === 'active' && (
                                <button className="btn btn-ghost btn-sm"
                                  style={{ fontSize: 11, color: 'var(--amber)' }}
                                  onClick={() => setRetireModal({ assistant: a, action: 'leaving' })}>
                                  退職予定
                                </button>
                              )}
                              {empStatus === 'leaving' && (
                                <>
                                  <button className="btn btn-ghost btn-sm"
                                    style={{ fontSize: 11, color: 'var(--red)' }}
                                    onClick={() => setRetireModal({ assistant: a, action: 'retired' })}>
                                    退職済みにする
                                  </button>
                                  <button className="btn btn-ghost btn-sm"
                                    style={{ fontSize: 11, color: 'var(--ink-3)' }}
                                    onClick={() => setRetireModal({ assistant: a, action: 'reactivate' })}>
                                    現役に戻す
                                  </button>
                                </>
                              )}
                              {empStatus === 'retired' && (
                                <button className="btn btn-ghost btn-sm"
                                  style={{ fontSize: 11, color: 'var(--ink-3)' }}
                                  onClick={() => setRetireModal({ assistant: a, action: 'reactivate' })}>
                                  現役に戻す
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
          )}
            {overviewFilter === 'retired' && (
              <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--ink-3)', borderTop: '1px solid var(--paper-2)' }}>
                ※ 退職済みアシスタントの過去の給与・日報・シフト履歴は各タブから確認できます
              </div>
            )}
        </div>
      )}

      {/* ── 給与サマリー ── */}
      {tab === 'salary' && salary && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <span className="card-title">{month} 給与サマリー</span>
              <span className="mono" style={{ fontSize: 14, fontWeight: 700 }}>振込合計 ¥{fmt(salary.grand_total)}</span>
            </div>
            {isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px' }}>
                {salary.assistants.map((a: any) => (
                  <div key={a.id} style={{ background: 'var(--paper-2)', borderRadius: 8, padding: '12px 14px', border: '1px solid var(--paper-3)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {a.name}
                        {a.employment_status === 'leaving' && (
                          <span style={{
                            marginLeft: 6, fontSize: 10, fontWeight: 700, padding: '1px 6px',
                            borderRadius: 999, color: 'var(--amber)', background: 'var(--amber-light)',
                          }}>退職予定</span>
                        )}
                        {a.employment_status === 'retired' && (
                          <span className="badge badge-gray" style={{ marginLeft: 6, fontSize: 10 }}>退職済み</span>
                        )}
                      </div>
                      <span className="mono" style={{ fontWeight: 700, fontSize: 15, color: 'var(--green)' }}>¥{fmt(a.total_pay)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 14, marginTop: 6, fontSize: 12, color: 'var(--ink-2)', flexWrap: 'wrap' }}>
                      <span>出勤 {a.days_worked}日</span>
                      <span>残業 {a.total_overtime_min}分</span>
                      <span>交通費 ¥{fmt(a.total_transport)}</span>
                      <span style={{ color: 'var(--ink-3)' }}>食事 {a.meal_count}回</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>名前</th><th>出勤日数</th><th>残業（分）</th><th>交通費計</th><th>食事回数</th><th>合計</th></tr>
                </thead>
                <tbody>
                  {salary.assistants.map((a: any) => (
                    <tr key={a.id}>
                      <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                        {a.name}
                        {a.employment_status === 'leaving' && (
                          <span style={{
                            marginLeft: 6, fontSize: 10, fontWeight: 700, padding: '1px 6px',
                            borderRadius: 999, color: 'var(--amber)', background: 'var(--amber-light)',
                          }}>退職予定</span>
                        )}
                        {a.employment_status === 'retired' && (
                          <span className="badge badge-gray" style={{ marginLeft: 6, fontSize: 10 }}>退職済み</span>
                        )}
                      </td>
                      <td className="mono">{a.days_worked}日</td>
                      <td className="mono">{a.total_overtime_min}分</td>
                      <td className="mono">¥{fmt(a.total_transport)}</td>
                      <td className="mono" style={{ color: 'var(--ink-3)' }}>{a.meal_count}回</td>
                      <td className="mono" style={{ fontWeight: 700 }}>¥{fmt(a.total_pay)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
            <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--ink-3)', borderTop: '1px solid var(--paper-2)' }}>
              ※ 食費は会社がモバイルオーダーで直接負担するため振込合計に含まれません
            </div>
          </div>
          <div className="card">
            <div className="card-header">
              <span className="card-title">勤怠明細 — {month}</span>
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>各レコードの「修正」から金額・時間を訂正できます</span>
            </div>
            {isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px' }}>
                {sortedAttendance.length === 0 && (
                  <div className="text-muted" style={{ textAlign: 'center', padding: 24 }}>勤怠記録なし</div>
                )}
                {sortedAttendance.map((rec: any) => (
                  <div key={rec.id} style={{ background: 'var(--paper-2)', borderRadius: 8, padding: '12px 14px', border: '1px solid var(--paper-3)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                      <div>
                        <span className="mono" style={{ fontWeight: 700, fontSize: 14 }}>{rec.work_date}</span>
                        <span style={{ marginLeft: 8, fontWeight: 500 }}>{rec.assistant_name}</span>
                      </div>
                      {rec.work_type === 'part_time'
                        ? <span className="badge badge-blue">時間</span>
                        : <span className="badge badge-gray">フル</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 14, marginTop: 8, flexWrap: 'wrap', fontSize: 12 }}>
                      <span>{rec.check_in} – {rec.check_out}</span>
                      {rec.work_type === 'part_time' && <span>{rec.work_minutes ?? 0}分</span>}
                      {rec.overtime_min > 0 && <span style={{ color: 'var(--amber)' }}>残業 {rec.overtime_min}分</span>}
                      {rec.transport_fee > 0 && <span>交通費 ¥{fmt(rec.transport_fee)}</span>}
                      <span style={{ color: 'var(--ink-3)' }}>食事{rec.meal_provided ? 'あり' : 'なし'}</span>
                      <span style={{ color: 'var(--green)', fontWeight: 700 }}>¥{fmt(rec.daily_total)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--paper-3)' }}>
                      <button className="btn btn-outline btn-sm" onClick={() => setEditAttendance(rec)}>修正</button>
                      <button
                        className="btn btn-outline btn-sm"
                        style={{ color: 'var(--red)' }}
                        onClick={() => setDeleteAttendConfirm({
                          id: rec.id,
                          label: `${rec.assistant_name} ${rec.work_date}`,
                        })}>
                        削除
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <SortTh label="日付" colKey="work_date" sort={attendSort} onSort={toggleAttendSort} />
                  <SortTh label="名前" colKey="assistant_name" sort={attendSort} onSort={toggleAttendSort} />
                  <SortTh label="区分" colKey="work_type" sort={attendSort} onSort={toggleAttendSort} />
                  <SortTh label="時間(分)" colKey="work_minutes" sort={attendSort} onSort={toggleAttendSort} />
                  <th>出勤</th><th>退勤</th>
                  <SortTh label="残業(分)" colKey="overtime_min" sort={attendSort} onSort={toggleAttendSort} />
                  <SortTh label="交通費" colKey="transport_fee" sort={attendSort} onSort={toggleAttendSort} />
                  <th>食事</th>
                  <SortTh label="振込額" colKey="daily_total" sort={attendSort} onSort={toggleAttendSort} />
                  <th></th>
                </tr></thead>
                <tbody>
                  {sortedAttendance.length === 0 && (
                    <tr><td colSpan={11} className="text-muted" style={{ textAlign: 'center', padding: 24 }}>勤怠記録なし</td></tr>
                  )}
                  {sortedAttendance.map((rec: any) => (
                    <tr key={rec.id}>
                      <td className="mono">{rec.work_date}</td>
                      <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{rec.assistant_name}</td>
                      <td style={{ fontSize: 11 }}>
                        {rec.work_type === 'part_time'
                          ? <span className="badge badge-blue">時間</span>
                          : <span className="badge badge-gray">フル</span>}
                      </td>
                      <td className="mono" style={{ color: 'var(--ink-3)' }}>
                        {rec.work_type === 'part_time' ? `${rec.work_minutes ?? 0}分` : '—'}
                      </td>
                      <td className="mono">{rec.check_in}</td>
                      <td className="mono">{rec.check_out}</td>
                      <td className="mono" style={{ color: rec.overtime_min > 0 ? 'var(--amber)' : undefined }}>{rec.overtime_min > 0 ? `${rec.overtime_min}分` : '—'}</td>
                      <td className="mono">{rec.transport_fee > 0 ? `¥${fmt(rec.transport_fee)}` : '—'}</td>
                      <td style={{ fontSize: 11, color: 'var(--ink-3)' }}>{rec.meal_provided ? 'あり' : 'なし'}</td>
                      <td className="mono" style={{ fontWeight: 600 }}>¥{fmt(rec.daily_total)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                            onClick={() => setEditAttendance(rec)}>修正</button>
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ fontSize: 11, color: 'var(--red)' }}
                            onClick={() => setDeleteAttendConfirm({
                              id: rec.id,
                              label: `${rec.assistant_name} ${rec.work_date}`,
                            })}>
                            削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        </div>
      )}

      {/* ── シフト希望 ── */}

      {tab === 'shifts' && shiftData && (
        <div>
          {/* ── 26日以降更新アラートバナー ── */}
          {(shiftData.needs_review_count ?? 0) > 0 && (
            <div style={{
              background: 'var(--amber-light)', border: '1px solid rgba(176,122,18,0.3)',
              borderRadius: 8, padding: '12px 16px', marginBottom: 16,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: 18 }}>📋</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--amber)' }}>
                  シフト希望の更新があります（{shiftData.needs_review_count}件）
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>
                  26日以降に更新されたシフト希望です。内容を確認して「確認済み」にしてください。
                </div>
              </div>
            </div>
          )}

          {displayMissingData && displayMissingData.count > 0 && (
            <MissingBanner missingData={displayMissingData} deadline={deadline} month={month} onDeadlineSet={load} onNotified={load} />
          )}
          {displayMissingData && displayMissingData.count === 0 && (
            <div style={{ background: 'var(--green-light)', border: '1px solid rgba(45,122,58,0.2)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: 'var(--green)', fontWeight: 600, fontSize: 13 }}>✓ 全員提出済み</span>
            </div>
          )}

          <div className="grid-3" style={{ marginBottom: 16 }}>
            <div className="metric">
              <div className="metric-label">提出済み</div>
              <div className={`metric-value ${shiftSubmittedCount === shiftEligibleTotal ? 'green' : 'amber'}`}>
                {shiftSubmittedCount} / {shiftEligibleTotal}名
              </div>
            </div>
            <div className="metric">
              <div className="metric-label">未提出</div>
              <div className={`metric-value ${displayMissingData?.count > 0 ? 'red' : 'green'}`}>{displayMissingData?.count ?? 0}名</div>
            </div>
            <div className="metric">
              <div className="metric-label">締切日</div>
              <div
                className={`metric-value ${displayMissingData?.count > 0 && displayMissingData?.is_overdue ? 'red' : ''}`}
                style={{ fontSize: 16 }}
              >
                {deadline?.deadline_date ?? '未設定'}
              </div>
              {displayMissingData?.count > 0 && displayMissingData?.is_overdue && (
                <div className="metric-sub" style={{ color: 'var(--red)' }}>期限超過</div>
              )}
              {displayMissingData?.count === 0 && deadline?.deadline_date && (
                <div className="metric-sub" style={{ color: 'var(--green)' }}>✓ 全員提出済み</div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">シフト希望一覧 — {month}</span></div>
            {shiftData.submitted.length === 0 ? (
              <div className="text-muted" style={{ textAlign: 'center', padding: 32 }}>提出なし</div>
            ) : isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 14px' }}>
                {shiftData.submitted.map((r: any) => {
                  const avail: string[] = JSON.parse(r.available_dates || '[]')
                  const needsReview = r.needs_review === 1
                  return (
                    <div key={r.id} className="card" style={{
                      padding: '14px 16px',
                      background: needsReview ? 'rgba(176,122,18,0.06)' : undefined,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 6 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>
                          {r.assistant_name}
                          {needsReview && (
                            <span className="badge badge-amber" style={{ marginLeft: 6, fontSize: 9, padding: '1px 5px' }}>要確認</span>
                          )}
                        </div>
                        {r.status === 'confirmed' ? <span className="badge badge-green">確定</span> : <span className="badge badge-amber">未確定</span>}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', rowGap: 6, columnGap: 8, marginTop: 10, fontSize: 13 }}>
                        <div style={{ color: 'var(--ink-3)' }}>担当</div>
                        <div><RoleBadge role={r.role_type} /></div>
                        <div style={{ color: 'var(--ink-3)' }}>希望日数</div>
                        <div className="mono">{avail.length > 0 ? `${avail.length}日` : '—'}</div>
                        <div style={{ color: 'var(--ink-3)' }}>希望日</div>
                        <div>{avail.length > 0 ? avail.map((d: string) => d.slice(8)).join(', ') + '日' : '—'}</div>
                      </div>
                      {r.note && (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--paper-3)' }}>
                          <div style={{ color: 'var(--ink-3)', fontSize: 11, marginBottom: 3 }}>備考</div>
                          <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{r.note}</div>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--paper-3)' }}>
                        <div style={{ fontSize: 11, color: needsReview ? 'var(--amber)' : 'var(--ink-3)' }}>
                          申請日　{r.submitted_at?.slice(0, 10) ?? '—'}
                          {r.updated_at && (
                            <span style={{ marginLeft: 8 }}>
                              更新　{new Date(r.updated_at + 'Z').toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                        {needsReview && (
                          <button
                            className="btn btn-sm btn-outline"
                            style={{ fontSize: 11, color: 'var(--amber)', borderColor: 'var(--amber)' }}
                            onClick={async () => {
                              await api.shiftRequests.markSeen(r.id)
                              load()
                            }}
                          >
                            確認済み
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>名前</th><th>役割</th><th>出勤日数</th><th>出勤できる日</th><th>備考</th><th>状態</th><th>提出日時</th><th>更新日時</th><th></th></tr></thead>
                <tbody>
                  {shiftData.submitted.map((r: any) => {
                    const avail: string[] = JSON.parse(r.available_dates || '[]')
                    const needsReview = r.needs_review === 1
                    return (
                      <tr key={r.id} style={{ background: needsReview ? 'rgba(176,122,18,0.06)' : undefined }}>
                        <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {r.assistant_name}
                          {needsReview && (
                            <span className="badge badge-amber" style={{ marginLeft: 6, fontSize: 9, padding: '1px 5px' }}>
                              要確認
                            </span>
                          )}
                        </td>
                        <td><RoleBadge role={r.role_type} /></td>
                        <td className="mono">{avail.length > 0 ? `${avail.length}日` : '—'}</td>
                        <td style={{ fontSize: 12 }}>{avail.length > 0 ? avail.map((d: string) => d.slice(8)).join(', ') + '日' : '—'}</td>
                        <td style={{ fontSize: 12, color: 'var(--ink-3)', maxWidth: 160 }}>{r.note ?? '—'}</td>
                        <td>{r.status === 'confirmed' ? <span className="badge badge-green">確定</span> : <span className="badge badge-amber">未確定</span>}</td>
                        <td className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{r.submitted_at?.slice(0, 10)}</td>
                        <td className="mono" style={{ fontSize: 11, color: needsReview ? 'var(--amber)' : 'var(--ink-3)' }}>
                          {r.updated_at
                            ? new Date(r.updated_at + 'Z').toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
                            : '—'}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {needsReview && (
                            <button
                              className="btn btn-sm btn-outline"
                              style={{ fontSize: 11, color: 'var(--amber)', borderColor: 'var(--amber)' }}
                              onClick={async () => {
                                await api.shiftRequests.markSeen(r.id)
                                load()
                              }}
                            >
                              確認済み
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            )}
          </div>

          {displayMissingData && displayMissingData.count > 0 && (
            <div className="card mt-4">
              <div className="card-header">
                <span className="card-title">未提出</span>
                <span className="badge badge-red">{displayMissingData.count}名</span>
              </div>
              <div style={{ padding: '12px 18px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {displayMissingData.missing.map((a: any) => <span key={a.id} className="badge badge-gray">{a.name}</span>)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── シフト確定 ── */}
      {tab === 'confirmed' && <ConfirmedShiftPanel month={month} />}

      {/* ── 支払準備 ── */}
      {tab === 'payment' && <PaymentPanel month={month} data={paymentData} onRefresh={load} />}

      {/* ── 給与申告確認 ── */}
      {tab === 'payroll' && <PayrollClaimsPanel month={month} />}

      {/* モーダル群 */}
      {showAttend && (
        <AttendanceModal assistants={attendancePickerAssistants} defaultDate={today}
          onClose={() => setShowAttend(false)}
          onSaved={(id, name, date) => { setShowAttend(false); load(); if (id) setUndoState({ type: 'attendance', id, label: `${name} ${date} の勤怠` }) }}
        />
      )}
      {editAssistant && (
        <AssistantEditModal assistant={editAssistant}
          onClose={() => setEditAssistant(null)}
          onSaved={() => { setEditAssistant(null); load(); loadOverview() }}
        />
      )}
      {retireModal && (
        <RetirementModal
          assistant={retireModal.assistant}
          action={retireModal.action}
          onClose={() => setRetireModal(null)}
          onSaved={() => { setRetireModal(null); load(); loadOverview() }}
        />
      )}
      {showOnboarding && (
        <AssistantOnboardingWizard
          onClose={() => setShowOnboarding(false)}
          onSaved={(_name) => { setShowOnboarding(false); load(); loadOverview() }}
        />
      )}
      {editAttendance && (
        <AttendanceModal assistants={attendancePickerAssistants} defaultDate={editAttendance.work_date}
          initialValues={{
            id: editAttendance.id, assistant_id: editAttendance.assistant_id,
            work_date: editAttendance.work_date, check_in: editAttendance.check_in,
            check_out: editAttendance.check_out, overtime_min: editAttendance.overtime_min,
            transport_fee: editAttendance.transport_fee, meal_provided: editAttendance.meal_provided,
            work_type: editAttendance.work_type, work_minutes: editAttendance.work_minutes,
            break_minutes: editAttendance.break_minutes,
            notes: editAttendance.notes,
          }}
          onClose={() => setEditAttendance(null)}
          onSaved={() => { setEditAttendance(null); load() }}
        />
      )}
      {attendToast && (
        <Toast msg={attendToast.msg} ok={attendToast.ok}
          onClose={() => setAttendToast(null)} />
      )}
      {deleteAttendConfirm && (
        <ConfirmDialog
          title="勤怠を削除しますか？"
          message={`「${deleteAttendConfirm.label}」の勤怠記録を削除します。\n給与サマリーが再計算されます。\nこの操作は取り消せません。`}
          confirmLabel="削除する"
          onConfirm={async () => {
            await doDeleteAttendance(deleteAttendConfirm.id)
            setDeleteAttendConfirm(null)
          }}
          onClose={() => setDeleteAttendConfirm(null)}
        />
      )}
      {undoState && (
        <UndoToast
          msg={`${undoState.label}を追加しました`}
          onUndo={async () => {
            if (undoState.type === 'attendance') await api.attendance.delete(undoState.id)
            await load()
          }}
          onClose={() => setUndoState(null)}
        />
      )}
    </div>
  )
}